// src/pages/Admin/Products/ProductFormModal.tsx
import React, { useState, useEffect, useRef } from 'react';
import { X, Save, RefreshCw, Grid, Package, ChevronDown, Clock, AlertCircle, AlertTriangle, Info, Plus, Minus, Edit, XCircle, Trash2 } from 'lucide-react';
import type { Product, ProductVariant, ProductImage, Store } from '../../types';
import ImageUpload from '../../components/admin/ImageUpload';
import AITagSuggest from '../../components/admin/AITagSuggest';
import { adminAPI } from '../../api/admin';
import ImageOrganizer from '../../components/admin/ImageOrganizer';
import debounce from 'lodash/debounce';
import DeleteVariantModal from '../../components/admin/DeleteVariantModal';
import { productsAPI } from '../../api/products';
import { vendorAPI } from '../../api/vendor';
import { getProductStatusBadge } from '../../utils/statusBadges';
import ProductReviewActions from './ProductReviewActions';
import { useAuth } from '../../contexts/AuthContext';
import ChangeAwareInput from '../common/ChangeAwareInput';
import ChangeAwarePriceInput from '../common/ChangeAwarePriceInput';
import { mergeProductImagesWithChanges, mergeProductVariantsWithChanges } from '../../utils/mergeChanges';
import { getVariantStatus } from '../../utils/variantChangeHelpers';
import { getStatusStyles, getVariantFieldChange } from '../../utils/variantHelpers';
import { convertSpecsToArray, convertSpecsToObject } from '../../utils/specUtils';
import ToggleSwitch from '../common/ToggleSwitch';
import { SaleControl } from '../admin/SaleControl';
import ConfirmationModal from '../common/ConfirmationModal';
import DeactivationReasonModal from '../admin/DeactivationReasonModal';
import { formatDateShortEnglish } from '../../utils/date';
import { SaleBanner } from '../admin/SaleBanner';
import SpecList from '../admin/SpecList';
import { getStatusIndicator } from '../../utils/productStatusHelpers';
import toast from '../../utils/toast';
import { getProductImageUrl } from '../../utils/productUtilities';
import { applySpecChanges, applyTagChanges, getFieldValue } from '../../utils/productChangeHelpers';
import StoreSelect from '../vendor/StoreSelect';
import { storesAPI } from '../../api/stores';
import { MEDIA_BASE_URL } from '../../api/auth';

interface ProductFormModalProps {
    product: Product | null;
    onClose: () => void;
    onSuccess: () => void;
    isVendor?: boolean;
    isStaff?: boolean;
    mode?: 'edit' | 'review' | 'view' | 'create';
    onProductStatusChange?: (productId: number, isActive: boolean) => void;
    onRollbackChanges?: (product: Product) => void;
}

interface Spec {
    id: string;  // unique identifier
    key: string;
    value: string;
}

interface FormErrors {
    // General tab errors
    store?: string;
    title?: string;
    sku?: string;
    selling_price?: string;
    cost_price?: string;
    description?: string;
    // Images tab errors
    images?: string;
    // Variants tab errors
    variant_images?: Record<number, string>;  // keyed by variant ID
    variant_specs?: Record<number, Record<number, { key?: string; value?: string }>>;
    variant_fields?: Record<number, { name?: string; sku_suffix?: string }>;
    variant_count?: string;
    // Specs errors
    specs?: Record<number, { key?: string; value?: string }>;
    // Keywords and categories errors
    search_keywords?: string;
    category_tags?: string;
}

const ProductFormModal: React.FC<ProductFormModalProps> = ({
    product,
    onClose,
    onSuccess,
    isVendor = false,
    isStaff = false,
    mode = 'edit',
    onProductStatusChange,
    onRollbackChanges,
}) => {
    const MAX_KEYWORDS = 25;
    const MAX_CATEGORIES = 5;
    const { user } = useAuth();
    const vendorCode = user?.vendor_code || '';
    const productVendorCode = product?.vendor_code || '';
    const vendorName = user?.vendor_profile?.business_name || '';
    const skuVendorCode = productVendorCode || vendorCode;
    const api = isVendor ? productsAPI : adminAPI; // Include this in a future version to ensure that vendors and admins use different APIs
    const [loading, setLoading] = useState(false);
    const isReviewMode = mode === 'review';
    const isViewMode = mode === 'view';
    const showChanges = isReviewMode && product?.status === 'pending_review';
    const isEditMode = mode === 'edit';
    const isCreateMode = mode === 'create';
    const isReadOnly = isReviewMode || mode === 'view' || (mode === 'edit' && product?.status === 'pending_review');
    const [activeTab, setActiveTab] = useState<'general' | 'variants' | 'images'>('general');
    const [showImageOrganizer, setShowImageOrganizer] = useState(false);
    const [expandedVariants, setExpandedVariants] = useState<Record<number, boolean>>({});
    const [showAddVariant, setShowAddVariant] = useState(false);
    const [autoSuggestEnabled, setAutoSuggestEnabled] = useState(isCreateMode);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [isTogglingStatus, setIsTogglingStatus] = useState(false);
    const [isTogglingVariantStatus, setIsTogglingVariantStatus] = useState<number | null>(null);
    const [showDeactivationModal, setShowDeactivationModal] = useState(false);
    const [stores, setStores] = useState<Store[]>([]);
    const [loadingStores, setLoadingStores] = useState(false);
    const [entityToDeactivate, setEntityToDeactivate] = useState<{
        type: 'product' | 'variant';
        id: number;
        name: string;
    } | null>(null);
    const [pendingToggleAction, setPendingToggleAction] = useState<{
        variantId?: number;
        checked: boolean;
    } | null>(null);
    const showSaveButtons = (mode === 'edit' && ['approved', 'draft', 'changes_requested'].includes(product?.status)) || (mode === 'create');
    const showReviewActions = isReviewMode && product?.status === 'pending_review';
    // const [availableCategories, setAvailableCategories] = useState<string[]>([]);
    const quickEditFields = ['selling_price', 'sale_expiry', 'discount_percent'];
    const [deleteVariantModal, setDeleteVariantModal] = useState<{
        isOpen: boolean;
        variantId: number | null;
        variantName: string;
        imagesCount: number;
        willHaveOneVariantAfterDelete: boolean;
    }>({
        isOpen: false,
        variantId: null,
        variantName: '',
        imagesCount: 0,
        willHaveOneVariantAfterDelete: false
    });
    const [newVariant, setNewVariant] = useState({
        name: '',
        sku_suffix: ''
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [errorTab, setErrorTab] = useState<'general' | 'variants' | 'images' | null>(null);
    const [specErrors, setSpecErrors] = useState<Record<number, { key?: string; value?: string }>>({});
    const [isSkuManuallyEdited, setIsSkuManuallyEdited] = useState(false);
    const sellingPriceRef = useRef<HTMLInputElement>(null);
    const costPriceRef = useRef<HTMLInputElement>(null);
    const [reviewData, setReviewData] = useState<any>(null);
    const [loadingReview, setLoadingReview] = useState(false);
    const [isVersionedProduct, setIsVersionedProduct] = useState(false);
    const [changeSummary, setChangeSummary] = useState<any>(null);
    const [pendingVersionId, setPendingVersionId] = useState<number | null>(null);
    const addVariantRef = useRef<HTMLDivElement>(null);
    const [confirmModal, setConfirmModal] = useState<{
        isOpen: boolean;
        title: string;
        message: React.ReactNode;
        confirmText?: string;
        cancelText?: string;
        type?: 'warning' | 'success' | 'danger' | 'info';
        onConfirm: () => void;
        onClose: () => void;
    }>({
        isOpen: false,
        title: '',
        message: '',
        onConfirm: () => { },
        onClose: () => { }
    });
    const [formData, setFormData] = useState<Partial<Product>>({
        specs: [] as Spec[],
        search_keywords: [] as string[],
        category_tags: [] as string[],
        images: [] as ProductImage[],
        variants: [] as ProductVariant[],
    });

    const [isLoadingSuggestions, setIsLoadingSuggestions] = useState({
        keywords: false,
        categories: false
    });

    const [specRecommendations, setSpecRecommendations] = useState<Array<{ key: string, is_required: boolean, category: string }>>([]);

    useEffect(() => {
        fetchStores();
    }, []);

    useEffect(() => {
        if (product?.id && (isReviewMode || product?.status === 'changes_requested')) {
            fetchReviewData();
        }
    }, [product?.id, isReviewMode, product?.status]);

    useEffect(() => {
        const categories = formData?.category_tags?.join(',');
        if (!categories) {
            setSpecRecommendations([]);
            return;
        }

        const fetchSpecRecommendations = async () => {
            try {
                const response = await productsAPI.getCategoryAttributes(categories);
                if (response.recommendations) {
                    setSpecRecommendations(response.recommendations);
                }
            } catch (error) {
                console.error('Failed to fetch spec recommendations:', error);
            }
        };

        const timer = setTimeout(fetchSpecRecommendations, 2500);
        return () => clearTimeout(timer);
    }, [formData?.category_tags]);

    const fetchStores = async () => {
        if (isCreateMode) {
            setLoadingStores(true);
            try {
                const data = await storesAPI.getStores();
                setStores(data);

                if (data.length === 1) {
                    setFormData(prev => ({ ...prev, store: data[0].id }));
                }
            } catch (error) {
                console.error('Failed to fetch stores:', error);
            } finally {
                setLoadingStores(false);
            }
        } else if (product?.store_detail) {
            setStores([product.store_detail]);
            setFormData(prev => ({ ...prev, store: product.store_detail?.id }));
        }
    };

    const fetchReviewData = async () => {
        setLoadingReview(true);
        if (!product)
            return;

        try {
            const response = await adminAPI.getPendingVersion(product.id);
            if (response.data) {
                // ✅ Set review data from the snapshot (proposed changes)
                setReviewData(response.data.snapshot);

                // ✅ Store change summary separately for display
                setChangeSummary(response.data.change_summary);

                // ✅ Store the version ID for review actions
                setPendingVersionId(response.data.id);
                setIsVersionedProduct(true);
            } else {
                setReviewData(null);
                setChangeSummary(null);
                setPendingVersionId(null);
                setIsVersionedProduct(false);
            }
        } catch (error) {
            console.error('Failed to fetch review data:', error);
            setReviewData(null);
            setChangeSummary(null);
            setPendingVersionId(null);
            setIsVersionedProduct(false);
        } finally {
            setLoadingReview(false);
        }
    };


    const generateSku = (title: string, vendorName?: string, vendorCode?: string): string => {
        if (!title) return '';

        // 1. Clean the title
        const cleanTitle = title
            .toUpperCase()
            .replace(/[^\w\s-]/g, '')
            .trim();

        if (!cleanTitle) return '';

        // 2. Generate product code
        const words = cleanTitle.split(/\s+/);
        let productCode = '';

        if (words.length === 1) {
            // Single word: take first 4-6 characters
            productCode = words[0].substring(0, 6);
        } else if (words.length === 2) {
            // Two words: take first 3 chars from first word, first 3 from second
            productCode = words[0].substring(0, 3) + words[1].substring(0, 3);
            productCode = productCode.substring(0, 6);
        } else {
            // Three or more words: take first 2 chars from first 3 words
            for (let i = 0; i < Math.min(3, words.length); i++) {
                productCode += words[i].substring(0, 2);
            }
            productCode = productCode.substring(0, 6);
        }

        // 3. Vendor prefix
        let prefix = '';
        if (vendorCode) {
            prefix = vendorCode.toUpperCase().substring(0, 4);
        } else if (vendorName) {
            const nameWords = vendorName.split(/\s+/);
            prefix = nameWords
                .slice(0, 3)
                .map(w => w[0])
                .join('')
                .toUpperCase()
                .substring(0, 4);
        }
        prefix = prefix || 'WT';

        // 4. Unique identifier (last 4 digits of timestamp)
        const uniqueId = Date.now().toString().slice(-4);
        clearFieldError('sku');

        return `${prefix}-${productCode}-${uniqueId}`;
    };


    // Auto-generate SKU when title changes (only for new products)
    useEffect(() => {
        // Handle new product initialization
        if (!product) {
            // Auto-generate SKU when title changes (only for new products)
            if (formData.title) {
                const newSku = generateSku(formData.title, vendorName, skuVendorCode);
                setIsSkuManuallyEdited(false);
                // Only update if SKU is empty or different
                if (!formData.sku || formData.sku !== newSku) {
                    setFormData(prev => ({ ...prev, sku: newSku }));
                }
            }

            // For new products, enable auto-suggest by default
            setAutoSuggestEnabled(true);
            setHasUserInteracted(false);
        } else {
            setIsSkuManuallyEdited(true);
        }


        // Initialize with one variant if none exist
        if (formData.variants.length === 0) {
            const tempId = -1;
            const initialVariant: ProductVariant = {
                id: tempId,
                name: formData.title || '', // Use title if available
                sku_suffix: '',
                full_sku: formData.sku ? `${formData.sku}-DEFAULT` : '',
                is_active: false,
                price: null,
                cost_price: null,
                description: '',
                specs: {},
            };
            setFormData(prev => ({
                ...prev,
                variants: [initialVariant]
            }));
        }

        // Handle existing product initialization
        if (product) {
            const applyPendingChanges =
                isReviewMode ||
                (product.status === 'changes_requested' && product?.can_resubmit);

            // Convert product images to Image format
            let productImages: ProductImage[] = (product.images || []).map((img) => ({
                id: img.id,
                image_url: img.image_url,
                is_primary: img.is_primary || false,
                position: img.position,
                alt_text: img.alt_text,
                product_variant: img.product_variant,
                is_local: img.is_local
            }));

            // Merge with pending changes if in review mode
            if (applyPendingChanges && changeSummary) {
                productImages = mergeProductImagesWithChanges(
                    productImages,
                    changeSummary,
                    isReviewMode,
                    true
                );
            }

            let formVariants: ProductVariant[] = (product.variants || []).map(v => {
                // Convert specs object to array

                let specs = convertSpecsToArray(v.specs);

                return ({
                    id: v.id,
                    name: v.name,
                    sku_suffix: v.sku_suffix,
                    full_sku: v.full_sku || `${product.sku}-${v.sku_suffix}`,
                    is_active: v.is_active,
                    stock: v.stock || [],
                    warehouse_stock: v.warehouse_stock || [],
                    images: v.images || [],
                    use_product_images: v.use_product_images,
                    price: v.price ?? null,
                    cost_price: v.cost_price ?? null,
                    description: v.description || '',
                    specs: specs,
                    sale_expiry: v.sale_expiry ?? null,
                    is_on_sale: v.is_on_sale,
                    discount_percent: v.discount_percent ?? null,
                    sale_start: v.sale_start || null,
                    deactivation_reason: v.deactivation_reason || null,
                    deactivated_at: v.deactivated_at || null,
                })
            });

            if (applyPendingChanges && changeSummary?.variants) {
                formVariants = mergeProductVariantsWithChanges(
                    formVariants,
                    changeSummary,
                    isReviewMode,
                    true
                );
            }

            let specsArray = Object.entries(product.specs || {}).map(([key, value]) => ({
                id: `spec-${Date.now()}-${Math.random()}`,
                key: key,
                value: typeof value === 'object' ? JSON.stringify(value) : String(value)
            }));

            if (applyPendingChanges && changeSummary?.specs) {
                specsArray = applySpecChanges(specsArray, changeSummary.specs);
            }

            // ✅ Apply pending changes for search_keywords
            let searchKeywords = product.search_keywords || [];
            if (applyPendingChanges && changeSummary?.search_keywords) {
                searchKeywords = applyTagChanges(searchKeywords, changeSummary.search_keywords);
            }

            // ✅ Apply pending changes for category_tags
            let categoryTags = product.category_tags || [];
            if (applyPendingChanges && changeSummary?.category_tags) {
                categoryTags = applyTagChanges(categoryTags, changeSummary.category_tags);
            }

            setFormData({
                store: product.store_detail?.id,
                sku: getFieldValue('sku', product.sku || '', changeSummary, applyPendingChanges ?? false),
                title: getFieldValue('title', product.title || '', changeSummary, applyPendingChanges ?? false),
                description: getFieldValue('description', product.description || '', changeSummary, applyPendingChanges ?? false),
                specs: specsArray,
                cost_price: getFieldValue('cost_price', product.cost_price || 0, changeSummary, applyPendingChanges ?? false),
                selling_price: getFieldValue('selling_price', product.selling_price, changeSummary, applyPendingChanges ?? false),
                is_active: getFieldValue('is_active', product.is_active ?? true, changeSummary, applyPendingChanges ?? false),
                search_keywords: searchKeywords,
                category_tags: categoryTags,
                is_featured: getFieldValue('is_featured', product.is_featured || false, changeSummary, applyPendingChanges ?? false),
                sale_start: getFieldValue('sale_start', product.sale_start, changeSummary, applyPendingChanges ?? false),
                sale_expiry: getFieldValue('sale_expiry', product.sale_expiry ? product.sale_expiry.split('T')[0] : '', changeSummary, applyPendingChanges ?? false),
                is_on_sale: getFieldValue('is_on_sale', product.is_on_sale, changeSummary, applyPendingChanges ?? false),
                discount_percent: getFieldValue('discount_percent', product.discount_percent || 0, changeSummary, applyPendingChanges ?? false),
                deactivation_reason: getFieldValue('deactivation_reason', product.deactivation_reason, changeSummary, applyPendingChanges ?? false),
                deactivated_at: getFieldValue('deactivated_at', product.deactivated_at, changeSummary, applyPendingChanges ?? false),
                images: productImages,
                variants: formVariants,
                has_variants: (product.variants && product.variants.length > 0) || product.has_variants || false,
                status: product.status,
            });

            // setFormData({
            //     sku: product.sku || '',
            //     title: product.title || '',
            //     description: product.description || '',
            //     specs: specsArray,
            //     cost_price: product.cost_price || 0,
            //     selling_price: product.selling_price,
            //     low_stock_threshold: product.low_stock_threshold || 5,
            //     hide_after_out_of_stock_days: product.hide_after_out_of_stock_days || 60,
            //     is_active: product.is_active ?? true,
            //     search_keywords: product.search_keywords || [],
            //     category_tags: product.category_tags || [],
            //     is_featured: product.is_featured || false,
            //     sale_start: product.sale_start,
            //     sale_expiry: product.sale_expiry ? product.sale_expiry.split('T')[0] : '',
            //     is_on_sale: product.is_on_sale,
            //     discount_percent: product.discount_percent || 0,
            //     deactivation_reason: product.deactivation_reason,
            //     deactivated_at: product.deactivated_at,
            //     images: productImages,
            //     variants: formVariants,
            //     has_variants: (product.variants && product.variants.length > 0) || product.has_variants || false
            // });

            // For existing products, disable auto-suggest by default
            // setAutoSuggestEnabled(false);
            setHasUserInteracted(false);
        }
    }, [product, changeSummary, isReviewMode]);

    // Auto-suggest keywords and categories when content changes
    useEffect(() => {
        // Don't auto-suggest unless enabled AND user has interacted
        if (!autoSuggestEnabled || !hasUserInteracted) {
            return;
        }
        const fetchSuggestions = async () => {
            if (!formData.title && !formData.description && Object.keys(formData.specs).length === 0) {
                return;
            }

            setIsLoadingSuggestions({ keywords: true, categories: true });

            try {
                // Fetch both in parallel
                const [keywordResponse, categoryResponse] = await Promise.all([
                    adminAPI.suggestTags({
                        title: formData.title,
                        description: formData.description,
                        specs: formData.specs,
                        type: 'keywords',
                        existing_tags: formData.search_keywords
                    }),
                    adminAPI.suggestTags({
                        title: formData.title,
                        description: formData.description,
                        specs: formData.specs,
                        type: 'categories',
                        existing_tags: formData.category_tags
                    })
                ]);
                setFormData(prev => ({
                    ...prev,
                    search_keywords: (keywordResponse.data?.suggestions?.length
                        ? keywordResponse.data.suggestions.slice(0, MAX_KEYWORDS)
                        : prev.search_keywords),
                    category_tags: (categoryResponse.data?.suggestions?.length
                        ? categoryResponse.data.suggestions.slice(0, MAX_CATEGORIES)
                        : prev.category_tags)
                }));

            } catch (error) {
                console.error('Failed to fetch suggestions:', error);
            } finally {
                setIsLoadingSuggestions({ keywords: false, categories: false });
            }
        };

        const debouncedFetch = debounce(fetchSuggestions, 1000);
        debouncedFetch();

        return () => {
            debouncedFetch.cancel();
        };
    }, [formData.title, formData.description, formData.specs, autoSuggestEnabled, hasUserInteracted]);

    useEffect(() => {
        // Only auto-generate SKU for new products
        if (!product && formData.title && !isSkuManuallyEdited) {
            const newSku = generateSku(formData.title, vendorName, skuVendorCode);
            // Only update if SKU is empty or different
            if (!formData.sku || formData.sku !== newSku) {
                setFormData(prev => ({ ...prev, sku: newSku }));
            }
        }

    }, [formData.title, product]);

    // ClearFieldError, also handles nested variant errors
    // const clearFieldError = (field: keyof FormErrors, variantId?: number, subField?: string) => {
    const clearFieldError = (field: keyof FormErrors, variantId?: number, specIndex?: number, fieldType?: string) => {
        if (field === 'variant_fields' && variantId && fieldType) {
            // Clear specific variant field error
            if (errors.variant_fields?.[variantId]?.[fieldType as keyof { name?: string; sku_suffix?: string }]) {
                const newErrors = { ...errors };
                if (newErrors.variant_fields?.[variantId]) {
                    delete newErrors.variant_fields[variantId][fieldType as keyof { name?: string; sku_suffix?: string }];
                    if (Object.keys(newErrors.variant_fields[variantId]).length === 0) {
                        delete newErrors.variant_fields[variantId];
                    }
                    if (Object.keys(newErrors.variant_fields || {}).length === 0) {
                        delete newErrors.variant_fields;
                    }
                    setErrors(newErrors);
                }
            }
            return;
        }

        if (field === 'specs' && specIndex !== undefined && fieldType) {
            // Clear product spec error
            if (errors.specs?.[specIndex]?.[fieldType as 'key' | 'value']) {
                const newErrors = { ...errors };
                delete newErrors.specs[specIndex][fieldType as 'key' | 'value'];
                if (Object.keys(newErrors.specs[specIndex]).length === 0) {
                    delete newErrors.specs[specIndex];
                }
                if (Object.keys(newErrors.specs || {}).length === 0) {
                    delete newErrors.specs;
                }
                setErrors(newErrors);
            }
            return;
        }

        if (field === 'variant_specs' && variantId && specIndex !== undefined && fieldType) {
            // Clear specific variant spec error
            if (errors.variant_specs?.[variantId]?.[specIndex]?.[fieldType as 'key' | 'value']) {
                const newErrors = { ...errors };
                if (newErrors.variant_specs?.[variantId]?.[specIndex]) {
                    delete newErrors.variant_specs[variantId][specIndex][fieldType as 'key' | 'value'];
                    if (Object.keys(newErrors.variant_specs[variantId][specIndex]).length === 0) {
                        delete newErrors.variant_specs[variantId][specIndex];
                    }
                    if (Object.keys(newErrors.variant_specs[variantId]).length === 0) {
                        delete newErrors.variant_specs[variantId];
                    }
                    if (Object.keys(newErrors.variant_specs || {}).length === 0) {
                        delete newErrors.variant_specs;
                    }
                    setErrors(newErrors);
                }
            }
            return;
        }

        if (field === 'variant_images' && variantId) {
            // Clear specific variant image error
            if (errors.variant_images?.[variantId]) {
                const newErrors = { ...errors };
                delete newErrors.variant_images?.[variantId];
                if (Object.keys(newErrors.variant_images || {}).length === 0) {
                    delete newErrors.variant_images;
                }
                setErrors(newErrors);
            }
            return;
        }

        // Regular field error clearing
        if (errors[field]) {
            const newErrors = { ...errors };
            delete newErrors[field];
            setErrors(newErrors);
        }
    };

    const handleSkuChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.toUpperCase();
        setFormData({ ...formData, sku: value });

        if (value === '') {
            setIsSkuManuallyEdited(false);
            // Auto-generate from title if title exists
            if (formData.title) {
                const newSku = generateSku(formData.title, vendorName, skuVendorCode);
                setFormData(prev => ({ ...prev, sku: newSku }));
            }
        } else {
            setIsSkuManuallyEdited(true);
        }

        // Mark SKU as manually edited
        setIsSkuManuallyEdited(true);

        // Clear SKU error when typing
        clearFieldError('sku');
    };

    // Track user interaction on title, description, and specs
    const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setHasUserInteracted(true);
        const value = e.target.value;
        setFormData({ ...formData, title: value });

        // Clear title error when typing
        clearFieldError('title');

        // Auto-generate SKU only if user hasn't manually edited SKU
        if (!isSkuManuallyEdited && value) {
            const newSku = generateSku(formData.title, vendorName, skuVendorCode);
            setFormData(prev => ({ ...prev, sku: newSku }));
        }
    };

    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setHasUserInteracted(true);
        setFormData({ ...formData, description: e.target.value });
    };

    const hasAnyErrors = (): boolean => {
        return hasTabErrors('general') || hasTabErrors('images') || hasTabErrors('variants')
    };

    const hasTabErrors = (tab: 'general' | 'images' | 'variants'): boolean => {
        switch (tab) {
            case 'general': {
                const generalFields = ['store', 'title', 'sku', 'selling_price', 'cost_price', 'description', 'specs', 'search_keywords', 'category_tags'];
                const hasGeneralError = generalFields.some(field => errors[field as keyof FormErrors]);
                // const hasSpecError = Object.keys(specErrors).length > 0;
                return hasGeneralError;
            }
            case 'images': {
                return !!errors.images;
            }
            case 'variants': {
                const hasVariantSpecErrors = errors.variant_specs ? Object.keys(errors.variant_specs).length > 0 : false;
                const hasVariantFieldErrors = errors.variant_fields ? Object.keys(errors.variant_fields).length > 0 : false;
                const hasVariantImageError = errors.variant_images ? Object.keys(errors.variant_images).length > 0 : false;
                const hasVariantCountError = errors.variant_count ? Object.keys(errors.variant_count).length > 0 : false;;

                return hasVariantSpecErrors || hasVariantFieldErrors || hasVariantImageError || hasVariantCountError;
            }
            default:
                return false;
        }
    };

    const hasTabChanges = (tab: 'general' | 'images' | 'variants'): boolean => {
        if (!isReviewMode) return false;

        switch (tab) {
            case 'general': {
                // Check if any general fields have changed
                const generalFields = ['title', 'sku', 'selling_price', 'cost_price', 'description', 'search_keywords', 'category_tags', 'specs'];
                return generalFields.some(field => {
                    const change = getFieldChange(field);
                    return change?.hasChanged || false;
                });
            }
            case 'images': {
                // Check if default images (product_variant === null) have changes
                if (!changeSummary?.images?.items) return false;

                const defaultImageChanges = changeSummary.images.items.filter(
                    item => !item.product_variant && !item.temp_variant_ref
                );

                return defaultImageChanges.some(item =>
                    item.action === 'added' ||
                    item.action === 'removed' ||
                    item.action === 'changed' ||
                    item.action === 'variant_moved' ||
                    item.action === 'primary_changed' ||
                    item.action === 'position_changed' ||
                    item.action === 'alt_text_changed'
                );
            }
            case 'variants': {
                // Check if variants changed (added, removed, or any fields changed)
                const variantChanges = changeSummary?.variants;

                // Check for variant image changes (even if variants object doesn't exist)
                const variantImageChanges = changeSummary?.images?.items?.some(
                    item => item.product_variant !== null && item.product_variant !== undefined &&
                        (item.action === 'added' || item.action === 'removed' || item.action === 'changed')
                ) || false;

                // If there are variant image changes, return true immediately
                if (variantImageChanges) return true;

                // If no variants object, return false (no variant changes)
                if (!variantChanges) return false;

                // const { variants } = variantChanges;

                // Check for added variants
                const hasAdded = variantChanges.added && variantChanges.added.length > 0;

                // Check for removed variants
                const hasRemoved = variantChanges.removed && variantChanges.removed.length > 0;

                // Check for changed variants (any field changes, including price/cost)
                const hasChanged = variantChanges.changed && variantChanges.changed.some(change => {
                    const changedFields = change.changed_fields || {};
                    return Object.keys(changedFields).length > 0;  // Any change counts
                });

                return hasAdded || hasRemoved || hasChanged;
            }
            default:
                return false;
        }
    };

    const getErrorTab = (errors: FormErrors): 'general' | 'variants' | 'images' | null => {
        // Check order: general → images → variants
        const errorKeys = Object.keys(errors);

        // General tab errors
        const generalFields = ['store', 'title', 'sku', 'description', 'specs', 'selling_price', 'cost_price', 'search_keywords', 'category_tags'];
        if (errorKeys.some(key => generalFields.includes(key))) {
            return 'general';
        }

        // Images tab errors
        if (errorKeys.includes('images')) {
            return 'images';
        }

        // Variants tab errors
        if ((errors.variant_specs && Object.keys(errors.variant_specs).length > 0) ||
            (errors.variant_fields && Object.keys(errors.variant_fields).length > 0) ||
            (errors.variant_images && Object.keys(errors.variant_images).length > 0) ||
            (errors.variant_count)) {
            return 'variants';
        }

        return null;
    };

    const validateSpecs = () => {
        const newSpecErrors: Record<number, { key?: string; value?: string }> = {};
        const keys = new Set<string>();

        formData.specs.forEach((spec, index) => {
            if (!spec.key.trim()) {
                newSpecErrors[index] = { ...newSpecErrors[index], key: 'Key is required' };
            }
            if (!spec.value.trim()) {
                newSpecErrors[index] = { ...newSpecErrors[index], value: 'Value is required' };
            }
            if (spec.key.trim() && keys.has(spec.key.trim())) {
                newSpecErrors[index] = { ...newSpecErrors[index], key: 'Duplicate key' };
            }
            if (spec.key.trim()) {
                keys.add(spec.key.trim());
            }
        });

        setSpecErrors(newSpecErrors);
        return Object.keys(newSpecErrors).length === 0;
    };

    const validateVariantFields = (): boolean => {
        const newErrors: Record<number, { name?: string; sku_suffix?: string }> = {};
        let hasErrors = false;

        const usedNames = new Set<string>();
        const usedSkuSuffixes = new Set<string>();

        formData.variants.forEach((variant, index) => {
            // Use index as fallback if variant.id is undefined
            const variantId = variant.id ?? index;
            const hasOtherData = variant.price !== null || variant.cost_price !== null ||
                (variant.description && variant.description.trim() !== '') ||
                (variant.specs && variant.specs.length > 0) ||
                formData.images.some(img => img.product_variant === variant.id);
            const name = variant.name?.trim() || '';
            const skuSuffix = variant.sku_suffix?.trim() || '';
            const shouldValidate = name !== '' || skuSuffix !== '' || hasOtherData;

            if (shouldValidate) {
                if (!name) {
                    newErrors[variantId] = { ...newErrors[variantId], name: 'Variant name is required' };
                    hasErrors = true;
                }
                if (!skuSuffix) {
                    newErrors[variantId] = { ...newErrors[variantId], sku_suffix: 'SKU suffix is required' };
                    hasErrors = true;
                }

                if (name && usedNames.has(name.toLowerCase())) {
                    newErrors[variantId] = { ...newErrors[variantId], name: 'Variant name must be unique' };
                    hasErrors = true;
                }
                usedNames.add(name.toLowerCase());

                if (skuSuffix && usedSkuSuffixes.has(skuSuffix.toLowerCase())) {
                    newErrors[variantId] = { ...newErrors[variantId], sku_suffix: 'SKU suffix must be unique' };
                    hasErrors = true;
                }
                usedSkuSuffixes.add(skuSuffix.toLowerCase());
            }
        });

        setErrors(prev => ({
            ...prev,
            variant_fields: hasErrors ? newErrors : undefined
        }));

        return !hasErrors;
    };


    const scrollToAddVariant = () => {
        setShowAddVariant(true);
        clearFieldError('variant_count')
        setTimeout(() => {
            addVariantRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Add a highlight class for flashing effect
            // setTimeout(() => {
            //     addVariantRef.current?.classList.remove('highlight-flash');
            // }, 1500);
        }, 100);
    };

    const validateVariantSpecs = (): boolean => {
        const newErrors: Record<number, Record<number, { key?: string; value?: string }>> = {};
        let hasErrors = false;

        formData.variants.forEach((variant, variantIndex) => {
            if (variant.specs && Array.isArray(variant.specs)) {
                const keys = new Set<string>();

                variant.specs.forEach((spec: any, specIndex: number) => {
                    if (!spec.key?.trim()) {
                        hasErrors = true;
                        if (!newErrors[variant.id]) newErrors[variant.id] = {};
                        newErrors[variant.id][specIndex] = {
                            ...newErrors[variant.id][specIndex],
                            key: 'Key is required'
                        };
                    }
                    if (!spec.value?.trim()) {
                        hasErrors = true;
                        if (!newErrors[variant.id]) newErrors[variant.id] = {};
                        newErrors[variant.id][specIndex] = {
                            ...newErrors[variant.id][specIndex],
                            value: 'Value is required'
                        };
                    }
                    if (spec.key?.trim() && keys.has(spec.key.trim())) {
                        hasErrors = true;
                        if (!newErrors[variant.id]) newErrors[variant.id] = {};
                        newErrors[variant.id][specIndex] = {
                            ...newErrors[variant.id][specIndex],
                            key: 'Duplicate key'
                        };
                    }
                    if (spec.key?.trim()) {
                        keys.add(spec.key.trim());
                    }
                });
            }
        });
        // Set the error in the main errors state
        setErrors(prev => ({
            ...prev,
            variant_specs: hasErrors ? newErrors : undefined
        }));

        return !hasErrors;
    };

    const validateVariantImages = (): boolean => {
        const newErrors: Record<number, string> = {};
        let hasErrors = false;

        formData.variants.forEach((variant) => {
            // Only validate if variant has an ID (existing or temp)
            if (variant.id) {
                const variantImages = formData.images.filter(img => img.product_variant === variant.id);
                if (variantImages.length === 0 && formData.variants.length > 1 && !variant.use_product_images) {
                    newErrors[variant.id] = `"${variant.name}" needs at least one image`;
                    hasErrors = true;
                }
            }
        });

        setErrors(prev => ({
            ...prev,
            variant_images: hasErrors ? newErrors : undefined
        }));

        return !hasErrors;
    };

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};
        const specsValid = validateSpecs();
        const variantFieldsValid = validateVariantFields();
        const variantSpecsValid = validateVariantSpecs();
        const variantImagesValid = validateVariantImages();

        // General tab validation
        if (!formData.store) {
            newErrors.store = 'Please select a store';
        }
        if (formData.title === '' || formData.title.trim().length < 3) {
            newErrors.title = 'Title must be at least 3 characters';
        }

        if (!formData.sku || formData.sku.trim().length < 2) {
            newErrors.sku = 'SKU is required';
        }

        if (!formData.selling_price || formData.selling_price <= 0) {
            newErrors.selling_price = 'Selling price must be greater than 0';
        }

        // if (formData.cost_price && formData.selling_price && formData.cost_price > formData.selling_price) {
        //     newErrors.cost_price = 'Cost price cannot be higher than selling price';
        // }

        if (!specsValid) {
            newErrors.specs = specErrors;
        }

        if (formData.search_keywords.length === 0) {
            newErrors.search_keywords = 'At least one search keyword is recommended';
        }

        if (formData.category_tags.length === 0) {
            newErrors.category_tags = 'At least one category tag is recommended';
        }

        // Images tab validation
        if (formData.images.length === 0) {
            newErrors.images = 'At least one image is recommended';
        }
        const defaultImages = formData.images.filter(img => img.product_variant === null);
        if (defaultImages.length > 10) {
            newErrors.images = 'The maximum allowed general images is 10';
        }

        const variantFields = formData.variants || [];

        // Check if there's exactly 1 variant with both name and SKU filled
        if (variantFields.length === 1) {
            const variant = variantFields[0];
            const hasName = variant.name && variant.name.trim() !== '';
            const hasSku = variant.sku_suffix && variant.sku_suffix.trim() !== '';

            if (hasName && hasSku) {
                newErrors.variant_count = 'You have started creating a variant but haven\'t added a second one. Either complete the variant setup with at least 2 variants, or clear the variant fields if you don\'t want variants.';
            }
        }

        setErrors(prevErrors => ({
            ...prevErrors,
            ...newErrors
        }));

        return Object.keys(newErrors).length === 0 && specsValid && variantSpecsValid && variantFieldsValid && variantImagesValid;
    };

    const scrollToFirstError = () => {
        const errorKeys = Object.keys(errors);

        // Priority order for error fields
        const fieldOrder = [
            'store', 'title', 'sku', 'description', 'specs', 'selling_price', 'cost_price',
            'search_keywords', 'category_tags',
            'images',
            'variant_count'
        ];

        // Check for variant errors
        const hasVariantFieldErrors = errors.variant_fields && Object.keys(errors.variant_fields).length > 0;
        const hasVariantSpecErrors = errors.variant_specs && Object.keys(errors.variant_specs).length > 0;
        const hasVariantImageErrors = errors.variant_images && Object.keys(errors.variant_images).length > 0;

        // Find the first error in priority order
        let firstErrorField = fieldOrder.find(field => errorKeys.includes(field));

        // If no field error found, check variant errors
        if (!firstErrorField) {
            if (hasVariantFieldErrors) {
                // Get the first variant with a field error
                const firstVariantId = Object.keys(errors.variant_fields || {})[0];
                // ✅ Use name attribute to find the variant
                const variantElement = document.querySelector(`[name="variant-${firstVariantId}-name"]`);
                if (variantElement) {
                    const container = variantElement.closest('.variant-container');
                    if (container) {
                        container.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        setTimeout(() => {
                            (variantElement as HTMLElement).focus();
                        }, 300);
                    }
                    return;
                }
            }

            if (hasVariantSpecErrors) {
                // Get the first variant with spec errors
                const firstVariantId = Object.keys(errors.variant_specs || {})[0];
                // Get the first spec index that has an error for this variant
                const specErrors = errors.variant_specs?.[Number(firstVariantId)];
                const firstSpecIndex = specErrors ? Object.keys(specErrors)[0] : '0';

                // Use the actual spec index that has the error
                const specInput = document.querySelector(`[name="variant-${firstVariantId}-spec-${firstSpecIndex}-key"]`);
                if (specInput) {
                    const container = specInput.closest('.variant-container');
                    if (container) {
                        container.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        setTimeout(() => {
                            (specInput as HTMLElement).focus();
                        }, 300);
                    }
                    return;
                }
            }

            if (hasVariantImageErrors) {
                // Get the first variant with image errors
                const firstVariantId = Object.keys(errors.variant_images || {})[0];
                // Use name attribute to find the variant images section
                const imageSection = document.querySelector(`[data-field="variant-${firstVariantId}-images"]`);
                if (imageSection) {
                    imageSection.scrollIntoView({ behavior: 'smooth', block: 'center' })
                    const uploadButton = imageSection.querySelector('button');
                    if (uploadButton) {
                        setTimeout(() => (uploadButton as HTMLElement).focus(), 300);
                    }
                    return;
                }
            }
        }

        // Handle regular field errors
        if (firstErrorField) {
            let element = null;

            // Handle spec errors (nested structure)
            if (firstErrorField === 'specs' && errors.specs) {
                const specErrors = errors.specs;
                const firstIndex = Object.keys(specErrors)[0];
                const firstError = specErrors[firstIndex];

                // Determine which field (key or value) has the error
                const field = firstError?.key ? 'key' : 'value';
                element = document.querySelector(`[name="spec-${firstIndex}-${field}"]`);
            } else {
                // Regular field
                element = document.querySelector(`[name="${firstErrorField}"]`);
                if (!element) {
                    element = document.querySelector(`[data-field="${firstErrorField}"]`);
                }
            }

            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                setTimeout(() => {
                    (element as HTMLElement).focus();
                }, 300);
            }
        }
    };

    const handleSubmitForReview = (e: React.FormEvent) => {
        // Show confirmation first
        if (product?.status === 'approved')
            setConfirmModal({
                isOpen: true,
                title: 'Submit Changes for Review?',
                message: (
                    <div className="space-y-2">
                        <p>Your product will be <strong>temporarily hidden</strong> from customers while your changes are reviewed.</p>
                        <p className="text-sm text-gray-600">It will automatically become visible again once approved.</p>
                    </div>
                ),
                confirmText: 'Yes, Submit for Review',
                cancelText: 'Cancel',
                type: 'warning',
                onConfirm: () => {
                    setConfirmModal(prev => ({ ...prev, isOpen: false }));
                    // Validate and submit
                    handleSubmit(e);
                },
                onClose: () => {
                    setConfirmModal(prev => ({ ...prev, isOpen: false }));
                }
            });
        else
            handleSubmit(e);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const isValid = validateForm();
        if (!isValid) {
            // Switch to the tab with the first error
            const firstErrorTab = getErrorTab(errors);
            if (firstErrorTab) {
                setActiveTab(firstErrorTab);
                setErrorTab(firstErrorTab);

                // Scroll to first error after tab switch
                setTimeout(() => {
                    scrollToFirstError();
                }, 100);
            }
            return;
        }
        setLoading(true);

        try {
            // Convert specs array back to object for JSONField
            const specsObject = convertSpecsToObject(formData.specs);

            // Filter out empty variants (no name AND no sku_suffix)
            const nonEmptyVariants = formData.variants.filter(v => {
                const hasName = v.name && v.name.trim() !== '';
                const hasSku = v.sku_suffix && v.sku_suffix.trim() !== '';
                return hasName || hasSku;
            });

            // Clean variants - handle new variants with temp IDs
            const cleanVariants = nonEmptyVariants.map(v => {
                const variantData: any = {
                    name: v.name,
                    sku_suffix: v.sku_suffix,
                    is_active: Boolean(v.is_active),
                    price: v.price ? Number(v.price) : null,
                    cost_price: v.cost_price ? Number(v.cost_price) : null,
                    description: v.description || '',
                    specs: convertSpecsToObject(v.specs) || {},
                    use_product_images: v.use_product_images ?? false,
                    saleStart: v.sale_start,
                    saleExpiry: v.sale_expiry,
                    discount_percent: v.discount_percent,
                };

                // If it's an existing variant (positive ID), include the ID
                if (v.id > 0) {
                    variantData.id = v.id;
                } else {
                    // For new variants, send the temp ID for backend mapping
                    variantData.temp_id = v.id;
                }

                return variantData;
            });

            const fd = new FormData();
            // Clean images - handle temp variant references
            const cleanImages = formData.images?.map((img, idx) => {
                const cleanImg: any = {
                    // image_url: img.image_url,
                    position: Number(img.position) || 0,
                    alt_text: img.alt_text || null,
                    is_primary: Boolean(img.is_primary),
                };

                // Include ID for existing images
                if (img.id && img.id > 0) {
                    cleanImg.id = img.id;
                    cleanImg.image_url = img.image_url;
                } else if (img.file) {
                    // New image - send file
                    const key = `img_${idx}`;
                    fd.append(key, img.file);
                    cleanImg._file_key = key;
                    // cleanImg.image_url = '';   // Backend will generate
                }

                // Handle variant association
                if (img.product_variant) {
                    if (img.product_variant < 0) {
                        // This image belongs to a NEW variant - send temp ID
                        cleanImg.temp_variant_ref = img.product_variant;
                    } else {
                        // This image belongs to an EXISTING variant
                        cleanImg.product_variant = img.product_variant;
                    }
                } else {
                    // Default images belong to the product
                    // For new products, the backend will handle this
                    if (product?.id) {
                        cleanImg.product = product.id;
                    }
                }

                return cleanImg; //handle image uploads (blob urls)
            });

            const submitData = {
                store: formData.store,
                sku: formData.sku,
                title: formData.title,
                description: formData.description || '',
                specs: specsObject,
                cost_price: Number(formData.cost_price) || 0,
                selling_price: Number(formData.selling_price) || 0,
                is_active: Boolean(formData.is_active),
                search_keywords: Array.isArray(formData.search_keywords) ? formData.search_keywords : [],
                category_tags: Array.isArray(formData.category_tags) ? formData.category_tags : [],
                is_featured: Boolean(formData.is_featured),
                images: cleanImages,
                variants: cleanVariants,
                has_previous_approved_version: formData.has_previous_approved_version,
                saleStart: formData.sale_start,
                saleExpiry: formData.sale_expiry,
                discount_percent: formData.discount_percent,
            };
            // 3. Stringify everything into one 'data' field
            fd.append('data', JSON.stringify(submitData));

            const response = await (product
                ? productsAPI.updateProduct(product.id, fd)
                : productsAPI.createProduct(fd));

            // Check for "no changes" response
            if (response.data?.message === 'No changes detected') {
                toast.info('No changes detected. Product remains unchanged.');
                onClose();
                onSuccess?.();
                return;
            }

            // Now revoke - images are safely on server, we can free up browser memory
            formData.images?.forEach(img => {
                if (img.image_url?.startsWith('blob:')) {
                    URL.revokeObjectURL(img.image_url);
                }
            });
            onSuccess();
        } catch (error: any) {
            console.error('❌ Failed to save product:', error);
            if (error.response) {
                console.error('Error response:', error.response.data);
                alert(`Error: ${JSON.stringify(error.response.data)}`);
            } else if (error.request) {
                alert('No response from server. Please check your connection.');
            } else {
                alert(`Error: ${error.message}`);
            }
        } finally {
            setLoading(false);
        }
    };

    const regenerateSku = () => {
        if (formData.title) {
            const newSku = generateSku(formData.title, vendorName, skuVendorCode);
            setFormData({ ...formData, sku: newSku });
            setIsSkuManuallyEdited(true);
        }
    };

    const addVariant = () => {
        if (!newVariant.name?.trim() || !newVariant.sku_suffix?.trim()) {
            toast.error('Please enter both variant name and SKU suffix');
            return;
        }

        const trimmedName = newVariant.name.trim();
        const trimmedSuffix = newVariant.sku_suffix.toUpperCase().trim();

        // Check for duplicate names (case-insensitive)
        const nameExists = formData.variants.some(
            v => v.name?.toLowerCase() === trimmedName.toLowerCase()
        );

        if (nameExists) {
            toast.error('A variant with this name already exists. Please use a unique name.');
            return;
        }

        // Check for duplicate SKU suffixes (case-insensitive)
        const suffixExists = formData.variants.some(
            v => v.sku_suffix?.toUpperCase() === trimmedSuffix
        );

        if (suffixExists) {
            toast.error('This SKU suffix is already in use. Please use a unique suffix.');
            return;
        }

        // Generate temp ID
        const lowestId = formData.variants.reduce(
            (min, v) => (v.id < min ? v.id : min),
            0
        );
        const tempId = lowestId - 1;

        const newVariantData: ProductVariant = {
            id: tempId,
            name: trimmedName,
            sku_suffix: trimmedSuffix,
            full_sku: `${formData.sku}-${trimmedSuffix}`,
            is_active: false,
            price: null,
            cost_price: null,
            description: '',
            specs: [],
        };

        setFormData(prev => ({
            ...prev,
            variants: [...prev.variants, newVariantData],
            has_variants: true
        }));

        setNewVariant({ name: '', sku_suffix: '' });
        setShowAddVariant(false);
    };

    const confirmDeleteVariant = (variantId: number) => {
        const variantToDelete = formData.variants.find(v => v.id === variantId);
        if (!variantToDelete) return;

        const imagesForVariant = formData.images.filter(img => img.product_variant === variantId);
        const willHaveOneVariantAfterDelete = formData.variants.length === 2;

        setDeleteVariantModal({
            isOpen: true,
            variantId,
            variantName: variantToDelete.name,
            imagesCount: imagesForVariant.length,
            willHaveOneVariantAfterDelete
        });
    };

    const handleDeleteVariant = (moveImagesToDefault: boolean) => {
        const { variantId, imagesCount, willHaveOneVariantAfterDelete } = deleteVariantModal;
        if (!variantId) return;

        // Handle images based on user choice
        let updatedImages = [...formData.images];
        let toastMessage = '';
        let toastIcon = '🗑️';

        if (moveImagesToDefault) {
            // Move images to default
            // Get max position of existing default images
            const defaultImages = updatedImages.filter(img => img.product_variant === null);
            const maxPosition = defaultImages.reduce((max, img) => Math.max(max, img.position || 0), -1);
            let nextPosition = maxPosition + 1;
            if (willHaveOneVariantAfterDelete) {
                // Moving to default for all variants
                updatedImages = updatedImages.map(img => ({
                    ...img,
                    product_variant: null,
                    position: img.product_variant !== null ? nextPosition++ : img.position
                }));
                toastMessage = `Variant deleted. Product now has one variant - all images moved to default`;
                toastIcon = '📸';
            } else {
                // Just move this variant's images to default
                updatedImages = updatedImages.map(img =>
                    img.product_variant === variantId
                        ? { ...img, product_variant: null, position: nextPosition++ }
                        : img
                );
                toastMessage = imagesCount === 1 ? `Variant deleted. ${imagesCount} image moved to default` : `Variant deleted. ${imagesCount} image(s) moved to default`;
                toastIcon = '📸';
            }
        } else {
            // Delete images with the variant
            updatedImages = updatedImages.filter(img => img.product_variant !== variantId);
            toastMessage = willHaveOneVariantAfterDelete
                ? `Variant deleted. Product now has one variant - all variant images removed`
                : `Variant and ${imagesCount} image(s) deleted`;
        }

        // Remove the variant
        const updatedVariants = formData.variants.filter(v => v.id !== variantId);

        // Update state
        setFormData({
            ...formData,
            variants: updatedVariants,
            images: updatedImages,
            has_variants: updatedVariants.length > 1
        });

        // Close modal
        setDeleteVariantModal(prev => ({ ...prev, isOpen: false }));

        // Show single toast
        toast.success(toastMessage);//, { icon: toastIcon }
    };

    const updateVariant = (variantId: number, field: string, value: any) => {
        const updatedVariants = formData.variants.map(v => {
            if (v.id === variantId) {
                const updated = { ...v, [field]: value };
                // Ensure specs is always an object
                if (field === 'specs' && !value) {
                    updated.specs = {};
                }
                // Update full_sku if sku_suffix changes
                if (field === 'sku_suffix') {
                    updated.full_sku = `${formData.sku}-${value}`;
                }
                return updated;
            }
            return v;
        });

        setFormData({
            ...formData,
            variants: updatedVariants
        });
    };

    const getModalTitle = () => {
        // Create mode
        if (!product) {
            return 'Create New Product';
        }

        // Edit mode
        if (mode === 'edit') {
            return `Edit Product: ${product.title}`;
        }

        // Review mode
        if (mode === 'review') {
            return `Review Product: ${product.title}`;
        }

        // View mode
        if (mode === 'view') {
            return `View Product: ${product.title}`;
        }

        // Fallback
        return product ? `Product: ${product.title}` : 'Product';
    };

    const getTagPreviousValue = (fieldName: string) => {
        // Always use the product's current values as the baseline
        return product?.[fieldName] || [];
    };

    const getTagValue = (fieldName: string) => {
        if (isReviewMode && changeSummary && changeSummary[fieldName]) {
            const fieldData = changeSummary[fieldName];
            const items = fieldData.items || [];

            // Start with product's current values
            const currentValues = product?.[fieldName] || [];

            // Apply changes from summary
            const removedValues = items
                .filter(item => item.action === 'removed')
                .map(item => item.value);

            const addedValues = items
                .filter(item => item.action === 'added')
                .map(item => item.value);

            // Build final list: remove removed, add added
            let finalValues = currentValues.filter(v => !removedValues.includes(v));
            finalValues = [...finalValues, ...addedValues];

            return finalValues;
        }
        return formData[fieldName] || [];
    };

    const getFieldChange = (fieldName: string) => {
        if (!isReviewMode || !changeSummary) {
            return { value: formData[fieldName], previous: undefined, hasChanged: false };
        }

        const change = changeSummary[fieldName];

        if (change) {
            return {
                value: change.new,
                previous: change.old,
                hasChanged: true
            };
        }

        return {
            value: formData[fieldName],
            previous: undefined,
            hasChanged: false
        };
    };

    const handleToggleActive = async (checked: boolean) => {
        if (!product) return;

        // If deactivating and user is admin, show deactivation reason modal
        if (!checked && !isVendor) {
            setEntityToDeactivate({
                type: 'product',
                id: product.id,
                name: formData.title,
            });
            setPendingToggleAction({ checked });
            setShowDeactivationModal(true);
            return;
        }

        // If activating or vendor, proceed normally
        await performProductToggle(checked);
    };

    const performProductToggle = async (checked: boolean, reason?: string) => {
        if (!product) return;

        try {
            setLoading(true);
            setIsTogglingStatus(true);
            // Optimistic update
            setFormData(prev => ({ ...prev, is_active: checked }));

            const payload = isVendor
                ? { is_active: checked }
                : { is_active: checked, reason: reason };

            await (isVendor
                ? productsAPI.vendorToggleProductActive(product.id, checked)
                : adminAPI.adminToggleProductActive(product.id, payload));

            onProductStatusChange?.(product.id, checked);
            toast.success(`Product ${checked ? 'activated' : 'deactivated'} successfully`);
        } catch (error) {
            setFormData(prev => ({ ...prev, is_active: !checked }));
            toast.error('Failed to update product status');
        } finally {
            setLoading(false);
            setIsTogglingStatus(false);
        }
    };

    const handleVariantToggleActive = async (variantId: number, checked: boolean) => {
        if (!product) return;

        // Check if this is the last active variant
        const activeVariants = formData.variants.filter(v => v.is_active);
        const isLastActive = !checked && activeVariants.length === 1;

        // If admin and deactivating, show deactivation reason modal
        if (!checked && !isVendor) {
            const variant = formData.variants.find(v => v.id === variantId);
            setEntityToDeactivate({
                type: 'variant',
                id: variantId,
                name: variant?.name || `Variant ${variantId}`,
            });
            setPendingToggleAction({ variantId, checked });
            setShowDeactivationModal(true);
            return;
        }

        // If vendor and deactivating the last active variant, show vendor confirmation
        if (isLastActive && isVendor) {
            return new Promise<void>((resolve) => {
                setConfirmModal({
                    isOpen: true,
                    title: 'Deactivate Last Active Variant?',
                    message: 'This is the only active variant. The product will be automatically deactivated.',
                    confirmText: 'Yes, deactivate',
                    cancelText: 'Cancel',
                    type: 'warning',
                    onConfirm: () => {
                        setConfirmModal(prev => ({ ...prev, isOpen: false }));
                        resolve(performVariantToggle(variantId, checked));
                    },
                    onClose: () => {
                        setConfirmModal(prev => ({ ...prev, isOpen: false }));
                        resolve();
                    }
                });
            });
        }

        // Otherwise proceed normally
        await performVariantToggle(variantId, checked);
    };

    const performVariantToggle = async (variantId: number, checked: boolean, reason?: string) => {
        if (!product) return;

        try {
            setLoading(true);
            setIsTogglingVariantStatus(variantId);
            // Optimistic update
            const updatedVariants = formData.variants.map(v =>
                v.id === variantId ? { ...v, is_active: checked } : v
            );

            const hasActiveVariant = updatedVariants.some(v => v.is_active);
            const productShouldBeActive = hasActiveVariant;

            setFormData(prev => ({
                ...prev,
                variants: updatedVariants,
                is_active: productShouldBeActive
            }));
            onProductStatusChange?.(product.id, productShouldBeActive);

            const payload = isVendor
                ? { is_active: checked }
                : { is_active: checked, reason: reason };

            const response = await (isVendor
                ? productsAPI.vendorToggleVariantActive(product.id, variantId, checked)
                : adminAPI.adminToggleVariantActive(product.id, variantId, payload));

            if (response.data.product_auto_deactivated) {
                toast('Variant deactivated. Product was auto-deactivated.', {
                    icon: '⚠️',
                    style: {
                        background: '#fffbeb',
                        color: '#92400e'
                    }
                });
            } else if (response.data.product_auto_activated) {
                toast.success('Variant activated. Product was auto-activated.');
            } else {
                toast.success(`Variant ${checked ? 'activated' : 'deactivated'} successfully`);
            }

        } catch (error) {
            setFormData(prev => ({
                ...prev,
                variants: prev.variants.map(v =>
                    v.id === variantId ? { ...v, is_active: !checked } : v
                )
            }));
            toast.error('Failed to update variant status');
        } finally {
            setLoading(false);
            setIsTogglingVariantStatus(null);
        }
    };

    // Deactivation modal confirm handler
    const handleDeactivationConfirm = (reason: string) => {
        setShowDeactivationModal(false);

        if (pendingToggleAction?.variantId !== undefined) {
            // Variant deactivation
            performVariantToggle(pendingToggleAction.variantId, pendingToggleAction.checked, reason);
        } else {
            // Product deactivation
            performProductToggle(pendingToggleAction?.checked || false, reason);
        }

        setPendingToggleAction(null);
        setEntityToDeactivate(null);
    };

    // Deactivation modal close handler
    const handleDeactivationModalClose = () => {
        setShowDeactivationModal(false);
        setPendingToggleAction(null);
        setEntityToDeactivate(null);
    };

    // Handle starting a sale on the product
    const handleStartSale = async (saleData: {
        discount: number;
        discountType: 'percentage' | 'fixed';
        startDate: Date;
        endDate: Date;
        variantId?: number | null;
    }) => {
        try {
            setLoading(true);

            const payload = {
                discount_percent: saleData.discount,
                sale_start: saleData.startDate.toISOString(),
                sale_expiry: saleData.endDate.toISOString(),
            };

            // Use productsAPI (not adminAPI)
            let response;
            if (saleData.variantId) {
                // Start variant sale
                response = await vendorAPI.startVariantSale(
                    product.id,
                    saleData.variantId,
                    payload
                );
            } else {
                // Start product sale
                response = await vendorAPI.startProductSale(product.id, payload);
            }

            // Update formData with sale info
            if (saleData.variantId) {
                const updatedVariants = formData.variants.map(v =>
                    v.id === saleData.variantId
                        ? { ...v, discount_percent: saleData.discount, sale_expiry: saleData.endDate.toISOString(), sale_start: saleData.startDate.toISOString() }
                        : v
                );
                setFormData(prev => ({ ...prev, variants: updatedVariants }));
            } else {
                setFormData(prev => ({
                    ...prev,
                    discount_percent: saleData.discount,
                    sale_start: saleData.startDate.toISOString(),
                    sale_expiry: saleData.endDate.toISOString()
                }));
            }

            toast.success('Sale started successfully!');
        } catch (error) {
            console.error('Failed to start sale:', error);
            toast.error('Failed to start sale. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleEndSaleClick = (variantId?: number | null) => {
        const saleName = variantId
            ? formData.variants?.find(v => v.id === variantId)?.name
            : formData.title;

        // Check if sale is scheduled (start date in future) or active
        const variant = variantId
            ? formData.variants?.find(v => v.id === variantId)
            : null;

        const saleStart = variant?.sale_start || formData.sale_start;
        const isScheduled = saleStart && new Date(saleStart) > new Date();

        const title = isScheduled ? 'Cancel Scheduled Sale?' : 'End Sale?';
        const message = isScheduled
            ? `Are you sure you want to cancel the scheduled sale for "${saleName}"?`
            : `Are you sure you want to end the active sale for "${saleName}"?`;
        const confirmText = isScheduled ? 'Yes, Cancel Sale' : 'Yes, End Sale';

        setConfirmModal({
            isOpen: true,
            title,
            message,
            confirmText,
            cancelText: 'Cancel',
            type: 'warning',
            onConfirm: () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                handleEndSale(variantId);
            },
            onClose: () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    // Handle ending a sale on the product
    const handleEndSale = async (variantId?: number | null) => {
        if (!product)
            return;
        try {
            setLoading(true);
            if (variantId) {
                // End variant sale
                await vendorAPI.endVariantSale(product.id, variantId);
            } else {
                // End product sale
                await vendorAPI.endProductSale(product.id);
            }

            // Remove sale info from formData
            if (variantId) {
                const updatedVariants = formData.variants?.map(v =>
                    v.id === variantId
                        ? { ...v, discount_percent: null, sale_start: null, sale_expiry: null, is_on_sale: false }
                        : v
                );
                setFormData(prev => ({ ...prev, variants: updatedVariants }));
            } else {
                setFormData(prev => ({
                    ...prev,
                    discount_percent: null,
                    sale_start: null,
                    sale_expiry: null,
                    is_on_sale: false,
                }));
            }

            toast.success('Sale ended successfully!');
        } catch (error) {
            console.error('Failed to end sale:', error);
            toast.error('Failed to end sale. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Handle variant sale (wrapper for variant-specific sales)
    const handleVariantSale = (variantId: number, saleData: any) => {
        handleStartSale({ ...saleData, variantId });
    };

    const productImageUrl = formData.images?.find(img => img.is_primary)?.image_url;
    const saleStart = formData.sale_start ? new Date(formData.sale_start) : null;
    const saleExpiry = formData.sale_expiry ? new Date(formData.sale_expiry) : null;
    const now = new Date();

    const isActive = saleExpiry && saleExpiry > now && saleStart && saleStart <= now;
    const isScheduled = saleStart && saleStart > now;

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

                <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                    <form>
                        {/* Header */}
                        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
                            <div className="flex items-center gap-3 min-w-0">
                                <h3 className="text-lg font-medium text-gray-900 truncate">
                                    {getModalTitle()}
                                </h3>

                                {/* Status Indicator */}
                                {product && getStatusIndicator({
                                    status: product.status,
                                    showTooltip: true,
                                    isStaff: isStaff
                                })}
                            </div>

                            <div className="flex items-center gap-3 shrink-0">
                                {product?.submission_count > 0 && (
                                    <p className="text-xs text-gray-500">
                                        {product?.submission_count} submission{product?.submission_count !== 1 ? 's' : ''}
                                    </p>
                                )}
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="text-gray-400 hover:text-gray-500"
                                >
                                    <X size={20} />
                                </button>
                            </div>
                        </div>
                        {product?.rejection_reason && (
                            <div className={`p-4 ${product.status === 'changes_requested' ? 'bg-orange-50 border border-orange-200' :
                                product.status === 'rejected' ? 'bg-red-100 border border-red-300' :
                                    'bg-yellow-50 border border-yellow-200'
                                }`}>
                                <div className="flex items-start gap-3">
                                    <AlertCircle className={`h-5 w-5 flex-shrink-0 mt-0.5 ${product.status === 'changes_requested' ? 'text-orange-600' :
                                        product.status === 'rejected' ? 'text-red-700' :
                                            'text-yellow-600'
                                        }`} />
                                    <div>
                                        {product.status === 'changes_requested' ? (
                                            // Vendor needs to make changes
                                            <>
                                                <p className="text-sm font-medium text-orange-800">Changes Requested</p>
                                                <p className="text-sm text-orange-700 mt-1">{product.rejection_reason}</p>
                                            </>
                                        ) : product.status === 'rejected' ? (
                                            // Product was rejected
                                            <>
                                                <p className="text-sm font-medium text-red-800">Product Rejected</p>
                                                <p className="text-sm text-red-700 mt-1">{product.rejection_reason}</p>
                                                {product.rejected_at && (
                                                    <p className="text-xs text-red-600 mt-1">
                                                        Rejected on {new Date(product.rejected_at).toLocaleDateString()}
                                                    </p>
                                                )}
                                            </>
                                        ) : product.status === 'pending_review' ? (
                                            // Admin/staff reviewing the product
                                            <>
                                                <p className="text-sm font-medium text-yellow-800">Previous Changes Requested</p>
                                                {isStaff && <p className="text-sm text-yellow-700 mt-1">
                                                    Vendor was asked to make these changes:
                                                </p>}
                                                <p className="text-sm text-gray-700 mt-1 italic">"{product.rejection_reason}"</p>
                                            </>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* Deactivation Reason */}
                        {(product?.deactivation_reason || formData.deactivation_reason) && (
                            <div className="p-4 bg-red-50 border border-red-200">
                                <div className="flex items-start gap-3">
                                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-medium text-red-800">Product Deactivated</p>
                                        <p className="text-sm text-red-700 mt-1">
                                            {product?.deactivation_reason || formData.deactivation_reason}
                                        </p>
                                        {product?.deactivated_at && (
                                            <p className="text-xs text-red-600 mt-1">
                                                Deactivated on {formatDateShortEnglish(product.deactivated_at)}
                                                {product?.deactivated_by && !isVendor && ` by ${product.deactivated_by}`}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                        {/* {isReviewMode && product && (
                            <div className="mb-0 p-3 border bg-yellow-50 border-yellow-200">
                                <div className="flex items-center gap-2">
                                    <Clock className="h-5 w-5 text-yellow-600" />
                                    <span className="font-medium text-yellow-800">
                                        Status: {getProductStatusBadge(product.status || 'draft').label}
                                    </span>
                                    <span className="text-sm text-yellow-700 ml-2">
                                        {isStaff ? 'Reviewing this product before making a decision' : 'This product is pending review'}
                                    </span>
                                </div>
                            </div>
                        )} */}

                        {loadingReview && (
                            <div className="flex justify-center items-center p-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                                <span className="ml-3 text-gray-500">Loading review data...</span>
                            </div>
                        )}
                        {/* Tabs */}
                        <div className="px-6 pt-2 border-b border-gray-200">
                            <nav className="flex space-x-4">
                                <button
                                    type="button"
                                    onClick={() => setActiveTab('general')}
                                    className={`px-3 py-2 text-sm font-medium rounded-t-lg ${activeTab === 'general'
                                        ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    General Info
                                    {hasTabErrors('general') && (
                                        <span className="inline-block ml-1 w-2 h-2 bg-red-500 rounded-full"
                                            title="This tab has errors that need to be fixed" />
                                    )}
                                    {hasTabChanges('general') && !hasTabErrors('general') && (
                                        <span className="inline-block ml-1 w-2 h-2 bg-blue-300 rounded-full"
                                            title="This tab has changes to review" />
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setActiveTab('images')}
                                    className={`px-3 py-2 text-sm font-medium rounded-t-lg ${activeTab === 'images'
                                        ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    Images ({formData.images?.length})
                                    {hasTabErrors('images') && (
                                        <span className="inline-block ml-1 w-2 h-2 bg-red-500 rounded-full"
                                            title="This tab has errors that need to be fixed" />
                                    )}
                                    {hasTabChanges('images') && !hasTabErrors('images') && (
                                        <span className="inline-block ml-1 w-2 h-2 bg-blue-300 rounded-full"
                                            title="This tab has changes to review" />
                                    )}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setActiveTab('variants')}
                                    className={`px-3 py-2 text-sm font-medium rounded-t-lg ${activeTab === 'variants'
                                        ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-600'
                                        : 'text-gray-500 hover:text-gray-700'
                                        }`}
                                >
                                    Variants {formData.variants.length > 1 ? '(' + formData.variants.length + ')' : ''}
                                    {hasTabErrors('variants') && (
                                        <span className="inline-block ml-1 w-2 h-2 bg-red-500 rounded-full"
                                            title="This tab has errors that need to be fixed" />
                                    )}
                                    {hasTabChanges('variants') && !hasTabErrors('variants') && (
                                        <span className="inline-block ml-1 w-2 h-2 bg-blue-300 rounded-full"
                                            title="This tab has changes to review" />
                                    )}
                                </button>
                            </nav>
                        </div>

                        {/* Body */}
                        <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
                            {activeTab === 'general' && (
                                <div className="space-y-6">
                                    <div className="grid grid-cols-3 gap-4 items-center">
                                        {/* Store Select */}
                                        <StoreSelect
                                            value={formData.store}
                                            onChange={(value) => setFormData({ ...formData, store: value })}
                                            placeholder="Select a store..."
                                            label="Store"
                                            error={errors.store}
                                            required
                                            disabled={!isCreateMode}
                                            note={stores.length > 1 ? "Assign this product to a store" : undefined}
                                            className={`${isCreateMode && 'mb-4'}`}
                                            stores={stores}
                                            loading={loadingStores}
                                            isCreateMode={isCreateMode}
                                        />
                                        {/* SKU */}
                                        <div className="col-span-1">
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                SKU *
                                                <span className="text-xs text-gray-400 ml-1">(optional - auto-generated if blank)</span>
                                            </label>

                                            <div className="flex gap-2">
                                                <ChangeAwareInput
                                                    name="sku"
                                                    type="text"
                                                    value={getFieldChange('sku').value ?? formData.sku}
                                                    previousValue={getFieldChange('sku').previous}
                                                    showChange={getFieldChange('sku').hasChanged}
                                                    onChange={handleSkuChange}
                                                    readOnly={isReadOnly}
                                                    error={errors.sku}
                                                    placeholder="Auto-generated from title"
                                                    disabled={!isCreateMode}
                                                    className={`flex-1 font-mono bg-gray-50 ${errors.sku ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                                                />
                                                {!isReadOnly && (
                                                    <button
                                                        type="button"
                                                        onClick={regenerateSku}
                                                        className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex-shrink-0"
                                                        title="Generate SKU from title"
                                                    >
                                                        <RefreshCw size={18} />
                                                    </button>
                                                )}
                                            </div>
                                            {!formData.sku && (
                                                <p className="text-xs text-gray-400 mt-1">
                                                    Leave blank to auto-generate from product title
                                                </p>
                                            )}
                                            {!isReviewMode && isSkuManuallyEdited && formData.sku && (
                                                <p className="text-xs text-blue-600 mt-1">
                                                    ✓ Custom SKU set. Auto-generation disabled.
                                                </p>
                                            )}
                                            {errors.sku && (
                                                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                                                    <AlertCircle className="h-4 w-4" />
                                                    {errors.sku}
                                                </p>
                                            )}
                                        </div>

                                        {/* Active Status */}
                                        <div className="pr-1 justify-end ml-auto">
                                            <div className="flex items-center gap-3">
                                                <span className="text-sm font-medium text-gray-700">Status:</span>
                                                <ToggleSwitch
                                                    checked={formData.is_active ?? true}
                                                    onChange={handleToggleActive}
                                                    disabled={(isReadOnly && isVendor) || product?.status !== 'approved' || product?.deactivation_reason ? true : false || isTogglingStatus}
                                                    name={"is_active"}
                                                    isLoading={isTogglingStatus}
                                                />
                                                <span className="text-sm text-gray-600">
                                                    {formData.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    {/* Title */}
                                    <div>
                                        <ChangeAwareInput
                                            label="Title"
                                            name="title"
                                            type="text"
                                            value={getFieldChange('title').value}
                                            previousValue={getFieldChange('title').previous}
                                            showChange={getFieldChange('title').hasChanged}
                                            onChange={handleTitleChange}
                                            readOnly={isReadOnly}
                                            error={errors.title}
                                            className={errors.title ? 'border-red-500 bg-red-50' : 'border-gray-300'}
                                            placeholder="Product title"
                                        />
                                    </div>

                                    {/* Description */}
                                    <div>
                                        <ChangeAwareInput
                                            label="Description"
                                            name="description"
                                            type="textarea"
                                            value={getFieldChange('description').value}
                                            previousValue={getFieldChange('description').previous}
                                            showChange={getFieldChange('description').hasChanged}
                                            onChange={handleDescriptionChange}
                                            readOnly={isReadOnly}
                                            error={errors.description}
                                            rows={4}
                                            className={errors.description ? 'border-red-500 bg-red-50' : 'border-gray-300'}
                                            placeholder="Describe your product..."
                                        />
                                    </div>

                                    {/* Specifications */}
                                    <SpecList
                                        specs={formData.specs || []}
                                        onChange={(newSpecs) => {
                                            setFormData({ ...formData, specs: newSpecs });
                                        }}
                                        onClearError={(index, fieldType) => {
                                            clearFieldError('specs', undefined, index, fieldType);
                                        }}
                                        recommendations={specRecommendations}
                                        disabled={isReadOnly}
                                        readOnly={isReadOnly}
                                        error={errors.specs}
                                        label="Specifications"
                                        showRecommendations={true}
                                        isReviewMode={isReviewMode}
                                        changeSummary={changeSummary}
                                        product={product}
                                    />

                                    {/* Pricing */}
                                    <div className="grid grid-cols-3 gap-6">
                                        <div>
                                            <div className="relative">
                                                <ChangeAwarePriceInput
                                                    ref={sellingPriceRef}
                                                    label="Selling Price"
                                                    name="selling_price"
                                                    value={getFieldChange('selling_price').value ?? formData.selling_price}
                                                    previousValue={getFieldChange('selling_price').previous}
                                                    showChange={getFieldChange('selling_price').hasChanged}
                                                    onChange={(value) => {
                                                        setFormData({ ...formData, selling_price: value });
                                                        clearFieldError('selling_price');
                                                    }}
                                                    required={true}
                                                    error={errors.selling_price}
                                                    disabled={isReadOnly || formData.is_on_sale}
                                                    readOnly={isReadOnly || formData.is_on_sale}
                                                    isReviewMode={isReviewMode}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <div className="relative">
                                                <ChangeAwarePriceInput
                                                    ref={costPriceRef}
                                                    label="Cost Price"
                                                    name="cost_price"
                                                    value={getFieldChange('cost_price').value ?? formData.cost_price}
                                                    previousValue={getFieldChange('cost_price').previous}
                                                    showChange={getFieldChange('cost_price').hasChanged}
                                                    onChange={(value) => {
                                                        setFormData({ ...formData, cost_price: value });
                                                        clearFieldError('cost_price');
                                                    }}
                                                    required={false}
                                                    error={errors.cost_price}
                                                    disabled={isReadOnly}
                                                    readOnly={isReadOnly}
                                                    note="Optional - helps track margins"
                                                    isReviewMode={isReviewMode}
                                                />
                                            </div>
                                            {
                                                (typeof formData.cost_price === 'number' &&
                                                    typeof formData.selling_price === 'number' &&
                                                    formData.cost_price > formData.selling_price) && (
                                                    <p className="text-sm text-yellow-600 flex items-center gap-1 mt-1">
                                                        <AlertTriangle className="h-4 w-4" />
                                                        Cost price is higher than selling price
                                                    </p>
                                                )
                                            }
                                        </div>
                                        <div className="flex justify-center items-center h-full">
                                            <SaleControl
                                                isActive={isActive}
                                                isScheduled={isScheduled}
                                                saleStart={formData.sale_start}
                                                onStartSale={handleStartSale}
                                                onEndSale={handleEndSaleClick}
                                                saleExpiry={formData.sale_expiry}
                                                discount={formData.discount_percent}
                                                basePrice={formData.selling_price || 0}
                                                productName={formData.title}
                                                productImage={productImageUrl ? `${MEDIA_BASE_URL}${productImageUrl}` : undefined}
                                                isVariant={false}
                                                disabled={!formData.is_active || product?.status !== 'approved' || !isVendor || !isEditMode}
                                            />
                                        </div>
                                    </div>

                                    {/* Sale Details Row - only shows when sale is active or scheduled*/}
                                    {formData.discount_percent && formData.sale_expiry && formData.is_active && (
                                        <SaleBanner
                                            discountPercent={formData.discount_percent ?? 0}
                                            saleExpiry={formData.sale_expiry ?? ''}
                                            saleStart={formData.sale_start}
                                            salePrice={(formData.selling_price ?? 1) * (1 - formData.discount_percent / 100)}
                                            onEndSale={() => handleEndSaleClick(null)}
                                            disabled={!isEditMode || !isVendor}
                                        />
                                    )}

                                    {/* AI Suggestions */}
                                    <AITagSuggest
                                        value={getTagValue('search_keywords')}
                                        // ✅ Previous value: from product (live data)
                                        previousValue={getTagPreviousValue('search_keywords')}
                                        onChange={(keywords) => {
                                            setFormData({ ...formData, search_keywords: keywords });
                                            clearFieldError('search_keywords');
                                        }}
                                        label="Search Keywords"
                                        placeholder="Add keyword or get AI suggestions..."
                                        type="keywords"
                                        productTitle={formData.title}
                                        productDescription={formData.description}
                                        productSpecs={formData.specs}
                                        isLoading={isLoadingSuggestions.keywords}
                                        maxTags={MAX_KEYWORDS}
                                        autoSuggestEnabled={autoSuggestEnabled}
                                        disabled={isReadOnly}
                                        error={errors.search_keywords}
                                        name="search_keywords"
                                        isReviewMode={isReviewMode}
                                    />
                                    <AITagSuggest
                                        value={getTagValue('category_tags')}
                                        // ✅ Previous value: from product (live data)
                                        previousValue={getTagPreviousValue('category_tags')}
                                        onChange={(categories) => {
                                            setFormData({ ...formData, category_tags: categories });
                                            clearFieldError('category_tags');
                                        }}
                                        label="Category Tags"
                                        placeholder="Add category or get AI suggestions..."
                                        type="categories"
                                        productTitle={formData.title}
                                        productDescription={formData.description}
                                        productSpecs={formData.specs}
                                        isLoading={isLoadingSuggestions.categories}
                                        maxTags={MAX_CATEGORIES}
                                        autoSuggestEnabled={autoSuggestEnabled}
                                        disabled={isReadOnly}
                                        error={errors.category_tags}
                                        name="category_tags"
                                        isReviewMode={isReviewMode}
                                    />
                                    {/* Featured & Status */}
                                    {/* <div className="space-y-3">
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id="is_featured"
                                                checked={formData.is_featured}
                                                onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                disabled={isReadOnly}
                                                readOnly={isReadOnly}
                                            />
                                            <label htmlFor="is_featured" className="ml-2 block text-sm text-gray-700">
                                                Featured product (shown prominently)
                                            </label>
                                        </div>
                                    </div> */}
                                    {
                                        (isCreateMode || isEditMode) &&
                                        <div className="border-t pt-4">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    <h4 className="text-sm font-medium text-gray-900">Auto AI Suggestions</h4>
                                                    <p className="text-xs text-gray-500">
                                                        Automatically suggest keywords and categories as you type
                                                    </p>
                                                </div>
                                                <label className="relative inline-flex items-center cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={autoSuggestEnabled}
                                                        onChange={(e) => setAutoSuggestEnabled(e.target.checked)}
                                                        className="sr-only peer"
                                                        disabled={isReadOnly}
                                                    />
                                                    <div className={`w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 ${isReadOnly ? 'opacity-50 cursor-not-allowed' : ''}`}></div>
                                                </label>
                                            </div>
                                            {autoSuggestEnabled && (
                                                <p className="text-xs text-green-600 mt-2">
                                                    ✓ AI suggestions will appear as you type in title, description, or specs
                                                </p>
                                            )}
                                        </div>
                                    }
                                </div>
                            )}

                            {activeTab === 'images' && (
                                <div className="space-y-6">
                                    <div className="flex justify-between items-center">
                                        <h3 className="text-lg font-medium">Product Images</h3>
                                        {formData.variants.length > 1 && !isReadOnly && (
                                            <button
                                                type="button"
                                                onClick={() => setShowImageOrganizer(true)}
                                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center"
                                            >
                                                <Grid size={18} className="mr-2" />
                                                Organize Images
                                            </button>
                                        )}
                                    </div>

                                    {/* Info box explaining the image structure */}
                                    {isEditMode &&
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <div className="flex items-start gap-3">
                                                <Package size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                                                <div className="space-y-2">
                                                    <p className="text-sm font-medium text-blue-800">
                                                        Understanding Product Images
                                                    </p>
                                                    <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                                                        <li>
                                                            <span className="font-medium">Default Images (below):</span> These are general product images shown for all variants.
                                                            Set the primary image here.
                                                        </li>
                                                        {formData.variants.length > 1 ? (
                                                            <li>
                                                                <span className="font-medium">Variant-Specific Images:</span> Additional images for specific variants can be added
                                                                in the <strong>Variants tab</strong> after creating variants. Use the <strong>Organize Images</strong> button to
                                                                move default images to specific variants.
                                                            </li>
                                                        ) : formData.variants.length === 1 ? (
                                                            <li>
                                                                <span className="font-medium">Single Variant Mode:</span> This product has one variant. All images here are default
                                                                images. Add more variants in the <strong>Variants tab</strong> to organize images separately.
                                                            </li>
                                                        ) : (
                                                            <li>
                                                                <span className="font-medium">No Variants Yet:</span> These are the only images for this product. Add variants in
                                                                the <strong>Variants tab</strong> to create variant-specific images.
                                                            </li>
                                                        )}
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    }

                                    {/* Show indicator for single variant mode - simpler version */}
                                    {formData.variants.length <= 1 && isEditMode && (
                                        <div className="bg-green-50 p-3 rounded-lg text-sm text-green-700 flex items-start border border-green-200">
                                            <Package size={18} className="mr-2 flex-shrink-0 mt-0.5 text-green-600" />
                                            <div>
                                                <p className="font-medium">Single Variant: {formData.variants[0]?.name}</p>
                                                <p className="text-green-600">
                                                    These default images will be shown for this variant.
                                                    Visit the <strong>Variants tab</strong> to add more variants and organize images separately.
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Default Images Upload Section */}
                                    <div className={isEditMode ? "border-t pt-4" : ""}>
                                        <div className="mb-3 flex items-center justify-between">
                                            <div>
                                                <h4 className="font-medium text-gray-900 flex items-center gap-2">
                                                    <Package size={18} className="text-gray-600" />
                                                    Default Images
                                                </h4>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    These images are shown for all variants. The primary image (star) will be the main product thumbnail.
                                                </p>
                                            </div>
                                            <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                                                {formData.images.filter(img => !img.product_variant).length} / 10 images
                                            </span>
                                        </div>
                                        {/* Images Tab - Single Component */}
                                        <ImageUpload
                                            key="product-images-tab"
                                            images={formData.images.filter(img => img.product_variant === null)}
                                            isReviewMode={isReviewMode || mode === 'view'}
                                            showChangeIndicators={isReviewMode}
                                            changeSummary={changeSummary?.images}
                                            onChange={(updatedImages) => {
                                                if (isReviewMode) return; // No-op in review mode

                                                const variantImages = formData.images.filter(img => img.product_variant);
                                                const updatedDefaultImagesWithNull = updatedImages.map(img => ({
                                                    ...img,
                                                    product_variant: null
                                                }));
                                                setFormData({
                                                    ...formData,
                                                    images: [...variantImages, ...updatedDefaultImagesWithNull]
                                                });
                                                clearFieldError('images');
                                            }}
                                            maxImages={10}
                                            variantId={null}
                                            variantName="Default Images"
                                            disabled={isReadOnly}
                                            name="images"
                                        />
                                    </div>

                                    {errors.images && (
                                        <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                                            <AlertCircle className="h-4 w-4" />
                                            {errors.images}
                                        </p>
                                    )}
                                    {/* Quick preview of variant images if they exist */}
                                    {formData.variants.length > 1 && (
                                        <div className="border-t pt-4 mt-4">
                                            <details className="group">
                                                <summary className="text-sm font-medium text-blue-600 cursor-pointer hover:text-blue-800 flex items-center gap-2">
                                                    <Package size={16} />
                                                    Show Variant-Specific Images ({formData.images.filter(img => img.product_variant).length} images across {formData.variants.length} variants)
                                                    <ChevronDown size={16} className="group-open:rotate-180 transition-transform" />
                                                </summary>
                                                <div className="mt-4 space-y-4">
                                                    {formData.variants.map(variant => {
                                                        const variantImages = formData.images.filter(img => img.product_variant === variant.id);

                                                        if (variantImages.length === 0) return null;

                                                        return (
                                                            <div key={variant.id} className="bg-gray-50 p-3 rounded-lg">
                                                                <div className="flex items-center justify-between mb-2">
                                                                    <span className="text-sm font-medium text-gray-700">{variant.name}</span>
                                                                    <span className="text-xs text-gray-500">{variantImages.length} images</span>
                                                                </div>
                                                                <div className="grid grid-cols-4 gap-2">
                                                                    {variantImages.slice(0, 3).map((img, idx) => (
                                                                        <div key={idx} className="aspect-square rounded overflow-hidden bg-gray-200">
                                                                            <img src={getProductImageUrl(img.image_url)} alt="" className="w-full h-full object-cover" />
                                                                        </div>
                                                                    ))}
                                                                    {variantImages.length > 3 && (
                                                                        <div className="aspect-square rounded bg-gray-100 flex items-center justify-center text-xs text-gray-500">
                                                                            +{variantImages.length - 3}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </details>
                                        </div>
                                    )}
                                </div>
                            )}


                            {activeTab === 'variants' && (
                                <div className="space-y-6">
                                    {/* Header with Add Variant button - always visible */}
                                    <div className="flex justify-between items-center">
                                        <h3 className="text-lg font-medium">Product Variants</h3>
                                        {!isReadOnly && (
                                            <button
                                                type="button"
                                                onClick={() => scrollToAddVariant()}
                                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
                                            >
                                                <Package size={18} className="mr-2" />
                                                Add Variant
                                            </button>)
                                        }
                                    </div>

                                    {/* Info banner explaining variant states */}
                                    {isEditMode &&
                                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                            <div className="flex items-start gap-3">
                                                <Package size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                                                <div className="space-y-1">
                                                    <p className="text-sm font-medium text-blue-800">
                                                        {formData.variants.length === 1 || formData.variants.length === 0
                                                            ? 'Single Product Mode'
                                                            : 'Multi-Variant Mode'}
                                                    </p>
                                                    <p className="text-sm text-blue-700">
                                                        {formData.variants.length === 1 || formData.variants.length === 0
                                                            ? 'This product has one configuration. All images are managed in the Images tab as product images.'
                                                            : `This product has ${formData.variants.length} variants. Each variant can have its own details.`}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                    {(isReviewMode || isViewMode) && (!formData.variants || formData.variants.length === 0) && (
                                        <div className="p-4 bg-gray-50 border border-gray-200">
                                            <div className="flex items-start gap-3">
                                                <Package className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />
                                                <div>
                                                    <p className="text-sm font-medium text-gray-700">No Variants</p>
                                                    <p className="text-sm text-gray-500 mt-0.5">
                                                        This product has no variants. It will be sold as a single product.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    {/* Variants List */}
                                    <div className="space-y-4">
                                        {formData.variants.map((variant) => {
                                            const variantImages = formData.images.filter(img => img.product_variant === variant.id);

                                            // Determine sale modal image
                                            const saleModalImage = variantImages.find(img => img.is_primary)?.image_url
                                                || variantImages[0]?.image_url
                                                || formData.images.find(img => img.is_primary)?.image_url;

                                            // Build full URL for the image
                                            const saleModalImageUrl = saleModalImage
                                                ? `${MEDIA_BASE_URL}${saleModalImage}`
                                                : undefined;

                                            const variantStatus = getVariantStatus(variant, isReviewMode);
                                            // const fullSpecs = buildVariantFullSpecsWithChanges(variant);
                                            const variantSaleStart = variant.sale_start ? new Date(variant.sale_start) : null;
                                            const variantSaleExpiry = variant.sale_expiry ? new Date(variant.sale_expiry) : null;

                                            const variantIsActive = variantSaleExpiry && variantSaleExpiry > now && variantSaleStart && variantSaleStart <= now;
                                            const variantIsScheduled = variantSaleStart && variantSaleStart > now;

                                            return (
                                                <div key={variant.id} className={`variant-container border-2 rounded-lg overflow-hidden ${getStatusStyles(variantStatus)}`}>
                                                    {/* Variant Header */}
                                                    <div className={`px-4 py-3 flex items-center justify-between ${variantStatus === 'added' ? 'bg-green-50' :
                                                        variantStatus === 'removed' ? 'bg-red-50' :
                                                            variantStatus === 'changed' ? 'bg-blue-50' :
                                                                'bg-gray-50'
                                                        }`}>
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-4">
                                                                {isReviewMode && variantStatus === 'added' && (
                                                                    <span className="px-2 py-1 bg-green-500 text-white text-xs rounded-full flex items-center gap-1">
                                                                        <Plus size={12} /> Added
                                                                    </span>
                                                                )}
                                                                {isReviewMode && variantStatus === 'removed' && (
                                                                    <span className="px-2 py-1 bg-red-500 text-white text-xs rounded-full flex items-center gap-1">
                                                                        <Minus size={12} /> Removed
                                                                    </span>
                                                                )}
                                                                {isReviewMode && variantStatus === 'changed' && (
                                                                    <span className="px-2 py-1 bg-blue-500 text-white text-xs rounded-full flex items-center gap-1">
                                                                        <Edit size={12} /> Modified
                                                                    </span>
                                                                )}

                                                                <div className="flex items-center text-sm text-gray-600">
                                                                    <span className="mr-2">Variant Name:</span>
                                                                    <ChangeAwareInput
                                                                        label=""
                                                                        name={`variant-${variant.id}-name`}
                                                                        type="text"
                                                                        value={getVariantFieldChange(variant, 'name', isReviewMode).value ?? variant.name}
                                                                        previousValue={getVariantFieldChange(variant, 'name', isReviewMode).previous}
                                                                        showChange={getVariantFieldChange(variant, 'name', isReviewMode).hasChanged}
                                                                        onChange={(e) => {
                                                                            updateVariant(variant.id, 'name', e.target.value);
                                                                            clearFieldError('variant_fields', variant.id, 'name');
                                                                        }}
                                                                        readOnly={isReadOnly}
                                                                        error={errors.variant_fields?.[variant.id]?.name}
                                                                        placeholder="Variant name"
                                                                        className="font-medium bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none px-1 py-0.5"
                                                                    />
                                                                </div>

                                                                <div className="flex items-center text-sm text-gray-600">
                                                                    <span className="mr-2">SKU Suffix:</span>
                                                                    <ChangeAwareInput
                                                                        label=""
                                                                        name={`variant-${variant.id}-sku`}
                                                                        type="text"
                                                                        value={getVariantFieldChange(variant, 'sku_suffix', isReviewMode).value ?? variant.sku_suffix}
                                                                        previousValue={getVariantFieldChange(variant, 'sku_suffix', isReviewMode).previous}
                                                                        showChange={getVariantFieldChange(variant, 'sku_suffix', isReviewMode).hasChanged}
                                                                        onChange={(e) => {
                                                                            updateVariant(variant.id, 'sku_suffix', e.target.value.toUpperCase());
                                                                            clearFieldError('variant_fields', variant.id, 'sku_suffix');
                                                                        }}
                                                                        readOnly={isReadOnly}
                                                                        error={errors.variant_fields?.[variant.id]?.sku_suffix}
                                                                        placeholder="SUFFIX"
                                                                        className="font-mono bg-transparent border-b border-transparent hover:border-gray-300 focus:border-blue-500 focus:outline-none px-1 py-0.5 w-24"
                                                                    />
                                                                </div>
                                                                {/* Active Status Toggle */}
                                                                <div className="flex items-start justify-end pt-1">
                                                                    <div className="flex items-center gap-3">
                                                                        <span className="text-sm text-gray-500">Status:</span>
                                                                        <ToggleSwitch
                                                                            checked={variant.is_active ?? true}
                                                                            onChange={(checked) => handleVariantToggleActive(variant.id, checked)}
                                                                            disabled={((isReadOnly && isVendor) || product?.status !== 'approved') || !formData?.is_active && variant.is_active || variant.deactivation_reason ? true : false || variant.id < 0 || isTogglingStatus}
                                                                            label={variant.is_active ? 'Active' : 'Inactive'}
                                                                            name="is_active"
                                                                            isLoading={variant.id === isTogglingVariantStatus}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {!isReadOnly && (
                                                            <button
                                                                type="button"
                                                                onClick={() => confirmDeleteVariant(variant.id)}
                                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                                title="Delete variant"
                                                            >
                                                                <X size={18} />
                                                            </button>
                                                        )}
                                                    </div>

                                                    {/* Deactivation Reason */}
                                                    {variant.deactivation_reason && (
                                                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                                            <div className="flex items-start gap-3">
                                                                <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0 mt-0.5" />
                                                                <div>
                                                                    <p className="text-xs font-medium text-red-800">Variant Deactivated</p>
                                                                    <p className="text-xs text-red-700 mt-0.5">{variant.deactivation_reason}</p>
                                                                    {variant.deactivated_at && (
                                                                        <p className="text-xs text-red-600 mt-0.5">
                                                                            Deactivated on {formatDateShortEnglish(variant.deactivated_at)}
                                                                            {variant.deactivated_by && !isVendor && ` by ${variant.deactivated_by}`}
                                                                        </p>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                    {/* Variant Options */}
                                                    <div className="p-4 border-t">
                                                        <div className="mb-3">
                                                            <p className="text-xs text-gray-500">
                                                                Optional Overrides <span className="text-gray-400">(leave blank to use product defaults)</span>
                                                            </p>
                                                        </div>
                                                        <div className="grid grid-cols-3 gap-4 mb-3 items-start">
                                                            <div>
                                                                <ChangeAwarePriceInput
                                                                    label="Variant Price"
                                                                    name={`variant-${variant.id}-price`}
                                                                    value={getVariantFieldChange(variant, 'price', isReviewMode).value ?? variant.price}
                                                                    previousValue={getVariantFieldChange(variant, 'price', isReviewMode).previous}
                                                                    showChange={getVariantFieldChange(variant, 'price', isReviewMode).hasChanged}
                                                                    onChange={(value) => {
                                                                        updateVariant(variant.id, 'price', value);
                                                                        clearFieldError('variant_fields', variant.id, 'price');
                                                                    }}
                                                                    placeholder="Use product price"
                                                                    note="Overrides product price"
                                                                    disabled={isReadOnly || formData.is_on_sale || variant.is_on_sale}
                                                                    readOnly={isReadOnly || formData.is_on_sale || variant.is_on_sale}
                                                                    error={errors.variant_fields?.[variant.id]?.price}
                                                                    isReviewMode={isReviewMode}
                                                                />
                                                            </div>

                                                            <div>
                                                                <ChangeAwarePriceInput
                                                                    label="Variant Cost"
                                                                    name={`variant-${variant.id}-cost_price`}
                                                                    value={getVariantFieldChange(variant, 'cost_price', isReviewMode).value ?? variant.cost_price}
                                                                    previousValue={getVariantFieldChange(variant, 'cost_price', isReviewMode).previous}
                                                                    showChange={getVariantFieldChange(variant, 'cost_price', isReviewMode).hasChanged}
                                                                    onChange={(value) => {
                                                                        updateVariant(variant.id, 'cost_price', value);
                                                                        clearFieldError('variant_fields', variant.id, 'cost_price');
                                                                    }}
                                                                    placeholder="Use product cost"
                                                                    note="Overrides product cost"
                                                                    disabled={isReadOnly}
                                                                    readOnly={isReadOnly}
                                                                    error={errors.variant_fields?.[variant.id]?.cost_price}
                                                                    isReviewMode={isReviewMode}
                                                                />
                                                            </div>

                                                            <div className="flex justify-center items-center h-full">
                                                                <SaleControl
                                                                    isActive={variantIsActive}
                                                                    isScheduled={variantIsScheduled}
                                                                    saleStart={variant.sale_start}
                                                                    onStartSale={(data) => handleVariantSale(variant.id, data)}
                                                                    onEndSale={() => handleEndSaleClick(variant.id)}
                                                                    saleExpiry={variant.sale_expiry}
                                                                    discount={variant.discount_percent}
                                                                    basePrice={variant.price || formData.selling_price || 0}
                                                                    productName={variant.name}
                                                                    productImage={saleModalImageUrl}
                                                                    isVariant={true}
                                                                    disabled={!formData.is_active || !variant.is_active || product?.status !== 'approved' || !isVendor || !isEditMode}
                                                                />
                                                            </div>
                                                        </div>
                                                        {/* Variant Sale Details Row */}
                                                        {variant.discount_percent && variant.sale_expiry && formData.is_active && (
                                                            <SaleBanner
                                                                discountPercent={variant.discount_percent}
                                                                saleStart={variant.sale_start}
                                                                saleExpiry={variant.sale_expiry}
                                                                salePrice={(variant.price || formData.selling_price || 0) * (1 - variant.discount_percent / 100)}
                                                                onEndSale={() => handleEndSaleClick(variant.id)}
                                                                variantId={variant.id}
                                                                disabled={!isEditMode || !isVendor}
                                                            />
                                                        )}

                                                        <div className="mb-3 mt-3">
                                                            <ChangeAwareInput
                                                                label="Variant Description"
                                                                name={`variant-${variant.id}-description`}
                                                                type="textarea"
                                                                value={(getVariantFieldChange(variant, 'description', isReviewMode).value ?? variant.description) || ''}
                                                                previousValue={getVariantFieldChange(variant, 'description', isReviewMode).previous}
                                                                showChange={getVariantFieldChange(variant, 'description', isReviewMode).hasChanged}
                                                                onChange={(e) => {
                                                                    updateVariant(variant.id, 'description', e.target.value);
                                                                    clearFieldError('variant_fields', variant.id, 'description');
                                                                }}
                                                                readOnly={isReadOnly}
                                                                error={errors.variant_fields?.[variant.id]?.description}
                                                                placeholder="Specific details about this variant..."
                                                                rows={2}
                                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                                            />
                                                        </div>

                                                        {/* Variant specs */}
                                                        <SpecList
                                                            specs={variant.specs || []}
                                                            onChange={(newSpecs) => {
                                                                updateVariant(variant.id, 'specs', newSpecs);
                                                            }}
                                                            onClearError={(index, fieldType) => {
                                                                clearFieldError('variant_specs', variant.id, index, fieldType);
                                                            }}
                                                            disabled={isReadOnly}
                                                            readOnly={isReadOnly}
                                                            error={errors.variant_specs?.[variant.id]}
                                                            label={`${variant.name} Variant Specs`}
                                                            showRecommendations={false}
                                                            namePrefix={`variant-${variant.id}-`}
                                                            showOverrideWarning={true}
                                                            parentSpecs={formData.specs}
                                                            isReviewMode={isReviewMode}
                                                            changeSummary={variant._action === 'changed' ? { specs: variant._changed_fields?.specs } : undefined}
                                                            product={product}
                                                        />

                                                        {/* Variant Images - Only show when there are 2+ variants */}
                                                        {formData.variants.length > 1 && (
                                                            <div className="mt-4 pt-4 border-t">
                                                                <div className="flex items-center justify-between mb-3">
                                                                    <h5 className="text-sm font-medium text-gray-700">
                                                                        {variant.name} Images
                                                                    </h5>
                                                                    <span className="text-xs text-gray-500">
                                                                        {variantImages.length} / 6 images
                                                                    </span>
                                                                </div>

                                                                {variantImages.length === 0 && !isReviewMode && !variant.use_product_images && (
                                                                    <div className="mb-3 text-xs text-blue-600 bg-blue-50 p-2 rounded border border-blue-200">
                                                                        Upload images specific to this variant. These will only show when
                                                                        customers select this option.
                                                                    </div>
                                                                )}

                                                                {/* Image Upload - only show if variant has images OR use_product_images is false */}
                                                                {(!variant.use_product_images || variantImages.length > 0) ? (
                                                                    <ImageUpload
                                                                        key={`variant-${variant.id}`}
                                                                        images={variantImages}
                                                                        onChange={(updatedVariantImages) => {
                                                                            const otherImages = formData.images.filter(
                                                                                img => img.product_variant !== variant.id
                                                                            );
                                                                            const newVariantImages = updatedVariantImages.map(img => ({
                                                                                ...img,
                                                                                product_variant: variant.id
                                                                            }));
                                                                            setFormData({
                                                                                ...formData,
                                                                                images: [...otherImages, ...newVariantImages]
                                                                            });
                                                                            clearFieldError('variant_images', variant.id);
                                                                        }}
                                                                        maxImages={6}
                                                                        variantId={variant.id}
                                                                        variantName={variant.name}
                                                                        name={`variant-${variant.id}-images`}
                                                                        disabled={isReadOnly}
                                                                        isReviewMode={isReviewMode}
                                                                        changeSummary={changeSummary?.images}
                                                                        showChangeIndicators={isReviewMode}
                                                                    />) : (
                                                                    !isReviewMode && !isViewMode ?
                                                                        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
                                                                            <p className="text-sm text-gray-500">
                                                                                Using product images for this variant.
                                                                                <br />
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => updateVariant(variant.id, 'use_product_images', false)}
                                                                                    className="text-blue-600 hover:underline text-xs mt-1"
                                                                                >
                                                                                    Upload variant-specific images instead
                                                                                </button>
                                                                            </p>
                                                                        </div> :
                                                                        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
                                                                            <p className="text-xs mt-1 text-gray-500">
                                                                                This variant is configured to use general product images.
                                                                            </p>
                                                                        </div>
                                                                )}
                                                                {errors.variant_images?.[variant.id] && (
                                                                    <p className="text-xs text-red-600 flex items-center gap-1">
                                                                        <AlertCircle className="h-3 w-3" />
                                                                        {errors.variant_images[variant.id]}
                                                                    </p>
                                                                )}
                                                                {variantImages.length === 0 && (
                                                                    <div className="mb-1 mt-3">
                                                                        {/* Toggle for "Use Product Images" */}
                                                                        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                                                                            <label className="flex items-center gap-2 cursor-pointer">
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={variant.use_product_images}
                                                                                    onChange={(e) => {
                                                                                        updateVariant(variant.id, 'use_product_images', e.target.checked);
                                                                                        clearFieldError('variant_images', variant.id);
                                                                                    }}
                                                                                    disabled={isReviewMode || isReadOnly || !isVendor}
                                                                                    className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                                                                />
                                                                                <span className="text-sm text-gray-700">Use product images</span>
                                                                            </label>
                                                                            <div className="relative group">
                                                                                <Info className="h-4 w-4 text-gray-400" />
                                                                                {/* Tooltip */}
                                                                                <div className="absolute z-10 invisible group-hover:visible bg-gray-300 text-gray-900 text-xs rounded p-2 max-w-sm w-64 break-words -top-2 left-1/2 -translate-x-1/2 transform -translate-y-full shadow-lg transition-all duration-200 delay-300">
                                                                                    Use default product images for this variant instead of uploading separate images.
                                                                                    <br />
                                                                                    <span className="text-gray-900">Recommended for size variants that look identical.</span>
                                                                                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-300 transform rotate-45"></div>
                                                                                </div>
                                                                            </div>
                                                                        </div>

                                                                        {/* Help text shown when toggle is checked */}
                                                                        {variant.use_product_images && !isReviewMode && !isViewMode && (
                                                                            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                                                                <p className="text-xs text-blue-700">
                                                                                    <strong>When to use this:</strong> If this variant looks identical to the main product (e.g.,
                                                                                    different sizes of the same shirt, different storage capacities of the same phone).
                                                                                    <br />
                                                                                    <span className="text-blue-600">✓</span> Saves time by reusing product images
                                                                                    <br />
                                                                                    <span className="text-blue-600">✓</span> Reduces duplicate image uploads
                                                                                </p>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        {/* Add Variant Form */}
                                        {showAddVariant && (
                                            <div className="border rounded-lg p-4 bg-gray-50" ref={addVariantRef}>
                                                <h4 className="font-medium mb-3">Add New Variant</h4>
                                                <div className="grid grid-cols-2 gap-4 mb-3">
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            Variant Name
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={newVariant.name}
                                                            onChange={(e) => setNewVariant({ ...newVariant, name: e.target.value })}
                                                            placeholder="e.g., Red, Large, 64GB"
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                                            autoFocus
                                                            disabled={isReadOnly}
                                                            readOnly={isReadOnly}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                                            SKU Suffix
                                                        </label>
                                                        <input
                                                            type="text"
                                                            value={newVariant.sku_suffix}
                                                            onChange={(e) => setNewVariant({ ...newVariant, sku_suffix: e.target.value.toUpperCase() })}
                                                            placeholder="e.g., RED, LARGE, 64GB"
                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono"
                                                            disabled={isReadOnly}
                                                            readOnly={isReadOnly}
                                                        />
                                                        <p className="text-xs text-gray-500 mt-1">
                                                            Full SKU: {formData.sku}-{newVariant.sku_suffix || 'SUFFIX'}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowAddVariant(false)}
                                                        className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={addVariant}
                                                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                                    >
                                                        Add Variant
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                        {!isReadOnly && formData.variants && formData.variants?.length >= 2 && (
                                            <div className="flex justify-end">
                                                <button
                                                    type="button"
                                                    onClick={() => scrollToAddVariant()}
                                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center"
                                                >
                                                    <Package size={18} className="mr-2" />
                                                    Add Variant
                                                </button>
                                            </div>
                                        )
                                        }
                                        {/* Show message when only one variant exists */}
                                        {formData.variants.length <= 1 && !isViewMode && !isReviewMode && (
                                            <div
                                                className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 cursor-pointer hover:bg-gray-100 transition-colors"
                                                onClick={() => scrollToAddVariant()}
                                                data-field="variant_count"
                                            >
                                                <Package size={40} className="mx-auto text-gray-400 mb-3" />
                                                <p className="text-gray-600 mb-2 font-medium">Add more variants?</p>
                                                <p className="text-sm text-gray-500 max-w-md mx-auto">
                                                    Your product currently has one configuration. Add more variants for different
                                                    options like sizes, colors, or models. Once you have at least 2 variants,
                                                    each one can have its own specifications.
                                                </p>
                                            </div>
                                        )}
                                        {errors.variant_count && (
                                            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                                                <AlertCircle className="h-4 w-4" />
                                                {errors.variant_count}
                                            </p>
                                        )}
                                    </div>
                                </div>
                            )}
                            {/* Review Actions Section */}
                            {showReviewActions && isStaff && (
                                <ProductReviewActions
                                    productId={product.id}
                                    productTitle={product.title}
                                    vendorName={product.vendor_name || 'Unknown'}
                                    onSuccess={onSuccess}
                                    onClose={onClose}
                                    versionId={pendingVersionId ?? undefined}
                                />
                            )}

                        </div>

                        {/* Image Organizer Modal */}
                        {showImageOrganizer && (
                            <ImageOrganizer
                                images={formData.images}
                                variants={formData.variants}
                                expandedVariants={expandedVariants}
                                onExpandedVariantsChange={setExpandedVariants}
                                onImagesReorganized={(images, updatedVariants) => {
                                    setFormData({
                                        ...formData,
                                        images,
                                        variants: updatedVariants || formData.variants
                                    });
                                }}
                                onClose={() => setShowImageOrganizer(false)}
                            />
                        )}
                        {/* Footer */}
                        {!isReadOnly && showSaveButtons && (
                            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                                {/* Left side - Discard Changes (only for changes_requested) */}
                                {isVendor && product?.status === 'changes_requested' && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            onRollbackChanges?.(product);
                                        }}
                                        className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center gap-2"
                                    >
                                        <Trash2 size={16} />
                                        Discard Changes
                                    </button>
                                )}

                                {/* Right side - Save and Cancel */}
                                <div className="flex space-x-3 ml-auto">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSubmitForReview}
                                        disabled={loading}
                                        className={`flex items-center px-4 py-2 rounded-lg text-sm font-medium 
                                                ${hasAnyErrors()
                                                ? 'bg-red-600 hover:bg-red-700 text-white'
                                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                                            } disabled:opacity-50`}
                                    >
                                        <Save size={18} className="mr-2" />
                                        {loading ? 'Saving...' : hasAnyErrors() ? 'Fix Errors' :
                                            product?.status === 'changes_requested' ? 'Save Changes' :
                                                product?.status === 'draft' ? 'Save Changes' :
                                                    product ? 'Update Product' : 'Create Product'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </form>
                </div>
            </div>
            {/* Delete Variant Modal */}
            <DeleteVariantModal
                isOpen={deleteVariantModal.isOpen}
                onClose={() => setDeleteVariantModal(prev => ({ ...prev, isOpen: false }))}
                onConfirm={handleDeleteVariant}
                variantName={deleteVariantModal.variantName}
                imagesCount={deleteVariantModal.imagesCount}
                willHaveOneVariantAfterDelete={deleteVariantModal.willHaveOneVariantAfterDelete}
            />

            {/* Confirmation Modal */}
            {confirmModal.isOpen && (
                <ConfirmationModal
                    isOpen={confirmModal.isOpen}
                    onClose={confirmModal.onClose}
                    onConfirm={confirmModal.onConfirm}
                    title={confirmModal.title}
                    message={confirmModal.message}
                    confirmText={confirmModal.confirmText}
                    cancelText={confirmModal.cancelText}
                    type={confirmModal.type}
                />
            )}

            {/* Product Deactivation Modal */}
            {showDeactivationModal && entityToDeactivate && (
                <DeactivationReasonModal
                    isOpen={showDeactivationModal}
                    onClose={handleDeactivationModalClose}
                    onConfirm={handleDeactivationConfirm}
                    entityType={entityToDeactivate.type}
                    entityName={entityToDeactivate.name}
                    isProcessing={loading}
                    isLastActive={entityToDeactivate.type === 'variant' &&
                        formData.variants.filter(v => v.is_active).length === 1}
                />
            )}
        </div>
    );
};

export default ProductFormModal;